/**
 * PatchSyncEngine — Central patch-driven sync processor.
 *
 * Every mutation to the babelfont model goes through this class.
 * It keeps a Yjs Y.Doc in sync with the JSON, manages per-glyph
 * UndoManagers, maintains the change log, and broadcasts updates
 * to other windows via BroadcastChannel.
 */
import * as Y from 'yjs';
import { jsonToYDoc, fromYType, toYType, setYPath, deleteYPath, getYPath, normalizeValueForYDocWrite, INDEXED_MAP_KEYS, diffYArray, applyIndexedMapArray as applyIndexedMapArrayToYMap, ensureGlyphLayersMap } from './change-bridge-ydoc';
import { omitRestingLayerRuntimeKeys, RESTING_LAYER_IDENTITY_KEYS, RESTING_LAYER_RUNTIME_KEYS, toRestingLayerJson } from './resting-layer-json';
import { buildHistoryStackItems, createLogEntry, deriveGlyphName, deriveLayerId, deriveGlyphNameFromPath, deriveGlyphNamesFromPaths, deriveLayerIdFromPath, deriveLayerIdsFromPaths, deriveObjectInfoFromPath, getPathSegments, joinPathWithGlyphSeparator, normalizeWorkerReplayTargets, normalizeGlyphRenames, glyphRenamesForHistoryAction, normalizeChangeLogEntry, resolveCollaborationOriginatingLayer, resolveCommitOriginatingLayer, resolveHistoryTargetItem, resolveUndoSurfaceAffinity, resetLogCounter, isDerivedLayerChangePath } from './change-log';
import { Logger } from './logger';
import { collaborationMessageKey, createChangeLogEntriesFromCollaborationMessageEnvelope, createCollaborationMessageEnvelopeFromChangeLogEntries } from './collaboration-message';
import { windowRole } from './window-role';
import { withSuppressedModelRecording } from './babelfont-model';
import { diffFontDataToPatchPairs } from './font-data-diff';
import { getUndoRedoContext } from './undo-redo-context';
import { computeLayerRecompositionClosure, deriveEditKindsFromChangeLogEntries, shouldResettleDerivedLayersOnHistoryReplay } from './recomposition-closure';
const console = new Logger('PatchSyncEngine');
export class MetadataFreeRemoteUpdateError extends Error {
    constructor() {
        super('Refusing metadata-free non-noop remote Yjs update; semantic metadata is required for scoped model and compile convergence');
        this.name = 'MetadataFreeRemoteUpdateError';
    }
}
/**
 * Origin token used by Yjs transactions that represent same-user edits.
 * Linked windows should all be able to undo these changes.
 */
const USER_EDIT_ORIGIN = 'user-edit';
const SYSTEM_REMOTE_ORIGIN = 'system-remote';
const HISTORY_REPLAY_ORIGIN = 'history-replay';
const FONT_EDIT_ORIGIN = 'font-edit';
const GLYPH_EDIT_ORIGIN = 'glyph-edit';
const LAYER_EDIT_ORIGIN_PREFIX = 'layer-edit:';
function stateVectorsEqual(left, right) {
    return (left.length === right.length &&
        left.every((value, index) => value === right[index]));
}
const FONT_UNDO_HISTORY_KEY = '__font-undo-history__';
function removeHistoryItemFromStack(stack, itemId) {
    const index = stack.lastIndexOf(itemId);
    if (index >= 0) {
        stack.splice(index, 1);
    }
}
function cloneChangeLogEntryForHistoryAction(entry, historyAction, targetHistoryItemId, timestamp, windowId, windowRoleLabel) {
    const { semanticChangeLogEntries: _semanticChangeLogEntries, ...fields } = entry;
    return normalizeChangeLogEntry({
        ...fields,
        timestamp,
        windowId,
        windowRoleLabel,
        historyAction,
        targetHistoryItemId,
        transactionId: null,
        glyphRenames: glyphRenamesForHistoryAction(fields.glyphRenames, historyAction)
    });
}
function getSemanticEntriesForHistoryItem(item, historyAction, targetHistoryItemId, timestamp, windowId, windowRoleLabel) {
    const sourceEntries = item?.entries?.length
        ? item.entries[0].semanticChangeLogEntries?.length
            ? item.entries[0].semanticChangeLogEntries
            : item.entries
        : null;
    if (!sourceEntries?.length) {
        return null;
    }
    return sourceEntries.map((itemEntry) => cloneChangeLogEntryForHistoryAction(itemEntry, historyAction, targetHistoryItemId, timestamp, windowId, windowRoleLabel));
}
function getEffectiveEmissionEntries(changeLogEntries) {
    const semanticEntries = changeLogEntries.flatMap((entry) => entry.semanticChangeLogEntries ?? []);
    return semanticEntries.length ? semanticEntries : changeLogEntries;
}
const INDEXED_ARRAY_ORDER_KEYS = {
    anchors: 'anchorOrder',
    guides: 'guideOrder'
};
const GRANULAR_LAYER_ARRAY_KEYS = new Set(['anchors', 'guides']);
function getLayerManagerKey(glyphName, layerId) {
    return `${glyphName}@@${layerId}`;
}
function getLayerEditOrigin(glyphName, layerId) {
    return `${LAYER_EDIT_ORIGIN_PREFIX}${glyphName}@@${layerId}`;
}
function getLayerFingerprintTargetKey(glyphName, layerId) {
    return `${glyphName}@@${layerId}`;
}
function normalizeLayerSignatureNodeType(nodeType) {
    switch (nodeType) {
        case 'Move':
        case 'Line':
        case 'OffCurve':
        case 'Curve':
        case 'QCurve':
            return nodeType;
        default:
            return String(nodeType || 'Unknown');
    }
}
function getComponentReferenceFromShape(shape) {
    if (!shape || typeof shape !== 'object') {
        return '';
    }
    if ('Component' in shape &&
        shape.Component &&
        typeof shape.Component === 'object') {
        return String(shape.Component.reference || '');
    }
    return String(shape.reference || '');
}
function getPathLikeShape(shape) {
    if (!shape || typeof shape !== 'object') {
        return null;
    }
    if ('Path' in shape && shape.Path && typeof shape.Path === 'object') {
        return shape.Path;
    }
    if (Array.isArray(shape.nodes)) {
        return shape;
    }
    return null;
}
function getLayerFingerprintFromJson(layerJson) {
    if (!layerJson || typeof layerJson !== 'object') {
        return null;
    }
    const shapes = Array.isArray(layerJson.shapes) ? layerJson.shapes : [];
    const anchors = Array.isArray(layerJson.anchors) ? layerJson.anchors : [];
    const componentSignatures = shapes
        .filter((shape) => !!shape &&
        typeof shape === 'object' &&
        ('Component' in shape || 'reference' in shape))
        .map((shape) => `C:${getComponentReferenceFromShape(shape)}`);
    const pathSignatures = shapes
        .map((shape) => getPathLikeShape(shape))
        .filter((shape) => Boolean(shape))
        .map((pathShape) => {
        const nodes = Array.isArray(pathShape.nodes) ? pathShape.nodes : [];
        const nodeTypes = nodes.map((node) => normalizeLayerSignatureNodeType(node?.nodetype));
        const closedFlag = pathShape.closed === false ? '0' : '1';
        return `P:${closedFlag}:${nodeTypes.length}:${nodeTypes.join(',')}`;
    });
    const anchorSignatures = anchors
        .map((anchor) => `A:${String(anchor?.name || '')}`)
        .sort((a, b) => a.localeCompare(b));
    return [
        `components[${componentSignatures.join('|')}]`,
        `paths[${pathSignatures.join('|')}]`,
        `anchors[${anchorSignatures.join('|')}]`
    ].join(';');
}
/**
 * Central patch processor that keeps Yjs Y.Doc in sync with the
 * babelfont JSON object model.
 */
export class PatchSyncEngine {
    /**
     * Fast deep equality that is deterministic about object key order
     * (same semantics as the old _stableStringify comparison) but avoids
     * building intermediate normalized objects and stringifying.
     * Short-circuits on the first difference.
     */
    _isDeepEqual(a, b) {
        if (a === b)
            return true;
        if (typeof a !== typeof b)
            return false;
        if (a === null || b === null)
            return a === b;
        if (typeof a !== 'object')
            return a === b;
        if (Array.isArray(a)) {
            if (!Array.isArray(b) || a.length !== b.length) {
                return false;
            }
            for (let i = 0; i < a.length; i++) {
                if (!this._isDeepEqual(a[i], b[i]))
                    return false;
            }
            return true;
        }
        if (Array.isArray(b))
            return false;
        const aObj = a;
        const bObj = b;
        const aKeys = Object.keys(aObj).sort();
        const bKeys = Object.keys(bObj).sort();
        if (aKeys.length !== bKeys.length)
            return false;
        for (let i = 0; i < aKeys.length; i++) {
            if (aKeys[i] !== bKeys[i])
                return false;
            if (!this._isDeepEqual(aObj[aKeys[i]], bObj[bKeys[i]])) {
                return false;
            }
        }
        return true;
    }
    _getWindowRoleLabel() {
        return window.windowRole?.getRoleLabel() ?? windowRole.getRoleLabel();
    }
    _createHistoryItemId() {
        return `history-item-${this._nextHistoryItemId++}`;
    }
    _getCurrentHistoryItemId() {
        return this._txHistoryItemId ?? this._createHistoryItemId();
    }
    _collectLayerFingerprintSnapshot(targets) {
        const snapshot = new Map();
        const glyphs = this._fontJson?.glyphs;
        if (!Array.isArray(glyphs)) {
            return snapshot;
        }
        // Targeted fast path: when the caller scoped this to a small set of
        // (glyph, layer) pairs, do an indexed lookup instead of iterating
        // every glyph and every layer in the font. For 1058 glyphs with
        // ~5 layers each, this is the difference between 5000+ iterations
        // and N (typically 1-20) per call. _syncJsonFromYDoc fires this
        // twice (before/after) on every Yjs sync (undo, remote, scoped).
        if (targets?.length) {
            // Build glyph-name → glyph index once. Build lazily so we only
            // pay the O(glyphs) cost on the first miss; if every target's
            // glyph is at the head of the array, we can avoid even that.
            let glyphIndex = null;
            const ensureGlyphIndex = () => {
                if (glyphIndex)
                    return glyphIndex;
                const idx = new Map();
                for (const glyph of glyphs) {
                    const name = typeof glyph?.name === 'string' ? glyph.name : null;
                    if (name && !idx.has(name)) {
                        idx.set(name, glyph);
                    }
                }
                glyphIndex = idx;
                return idx;
            };
            const dedupe = new Set();
            for (const target of targets) {
                const glyphName = target?.glyphName;
                const layerId = target?.layerId;
                if (!glyphName || !layerId)
                    continue;
                const targetKey = getLayerFingerprintTargetKey(glyphName, layerId);
                if (dedupe.has(targetKey))
                    continue;
                dedupe.add(targetKey);
                const glyph = ensureGlyphIndex().get(glyphName);
                if (!glyph)
                    continue;
                const layers = Array.isArray(glyph.layers) ? glyph.layers : [];
                // Layer counts per glyph are tiny (typically 1-10). A linear
                // find here is faster than building a per-glyph index.
                const layer = layers.find((l) => typeof l?.id === 'string' && l.id === layerId);
                if (!layer)
                    continue;
                snapshot.set(targetKey, {
                    glyphName,
                    layerId,
                    fingerprint: getLayerFingerprintFromJson(layer)
                });
            }
            return snapshot;
        }
        // Untargeted path: full font scan. Used for full Yjs rebuilds.
        for (const glyph of glyphs) {
            const glyphName = typeof glyph?.name === 'string' ? glyph.name : null;
            if (!glyphName) {
                continue;
            }
            const layers = Array.isArray(glyph.layers) ? glyph.layers : [];
            for (const layer of layers) {
                const layerId = typeof layer?.id === 'string' ? layer.id : null;
                if (!layerId) {
                    continue;
                }
                const targetKey = getLayerFingerprintTargetKey(glyphName, layerId);
                snapshot.set(targetKey, {
                    glyphName,
                    layerId,
                    fingerprint: getLayerFingerprintFromJson(layer)
                });
            }
        }
        return snapshot;
    }
    _emitLayerFingerprintChangedEvents(previousSnapshot, nextSnapshot) {
        if (typeof window === 'undefined') {
            return;
        }
        const targetKeys = new Set([
            ...previousSnapshot.keys(),
            ...nextSnapshot.keys()
        ]);
        for (const targetKey of targetKeys) {
            const previous = previousSnapshot.get(targetKey) ?? null;
            const next = nextSnapshot.get(targetKey) ?? null;
            const previousFingerprint = previous?.fingerprint ?? null;
            const nextFingerprint = next?.fingerprint ?? null;
            if (previousFingerprint === nextFingerprint) {
                continue;
            }
            const glyphName = next?.glyphName ?? previous?.glyphName;
            const layerId = next?.layerId ?? previous?.layerId;
            if (!glyphName || !layerId) {
                continue;
            }
            window.dispatchEvent(new CustomEvent('layerFingerprintChanged', {
                detail: {
                    glyphName,
                    layerId
                }
            }));
        }
    }
    constructor(windowId) {
        /** Per-glyph undo managers (keyed by glyph name) */
        this._undoManagers = new Map();
        /** Per-layer undo managers (keyed by glyph@@layer) */
        this._layerUndoManagers = new Map();
        /** "Font-level" undo manager for axes/masters/instances/font properties */
        this._fontUndoManager = null;
        /** Local history-item stacks aligned with each undo-manager scope */
        this._undoHistoryStacks = new Map();
        /** Change log of all recorded changes */
        this._changeLog = [];
        /** Reference to the raw babelfont JSON (the one babelfont-model.ts wraps) */
        this._fontJson = null;
        /** Transaction nesting depth */
        this._txDepth = 0;
        /** Current transaction label (outermost) */
        this._txLabel = null;
        /** Current transaction ID */
        this._txId = null;
        /** Wall-clock start time for the outermost transaction. */
        this._txStartTimeMs = null;
        /** Next transaction ID counter */
        this._nextTxId = 1;
        /** Next logical history item counter */
        this._nextHistoryItemId = 1;
        /** Current transaction-level history item ID */
        this._txHistoryItemId = null;
        /** Optional summary for an assistant-grouped history item. */
        this._txHistorySummary = null;
        /** Presentation-only group ID for an assistant prompt. */
        this._txPromptGroupId = null;
        /** Optional explicit history target for the current transaction */
        this._txHistoryTarget = null;
        /** Compact state-vector captured at the start of the outermost transaction. */
        this._txStartStateVector = null;
        /** Buffered operations for the current outermost transaction */
        this._txBufferedOperations = [];
        /** Flag: currently applying remote update (suppress outbound broadcast) */
        this._isApplyingRemote = false;
        /**
         * Optional callback that receives every Yjs binary update (local and
         * remote) together with the ChangeLogEntry list so the compilation worker
         * can maintain its own Y.Doc without receiving the full font JSON on every
         * edit. Set via `setYjsWorkerCallback`.
         */
        this._yjsWorkerCallback = null;
        /** Flag: suppress Y.Doc sync (during initFromJson) */
        this._isSyncing = false;
        /** Callback when a remote change arrives (for UI refresh) */
        this._onRemoteChange = null;
        /** Flat log of local and remote collaboration messages */
        this._collaborationLog = [];
        /** Callback when the Y.Doc is updated locally (for broadcasting) */
        this._localUpdateListeners = new Set();
        /** Callbacks for committed local/remote changes after Yjs apply */
        this._committedChangeListeners = new Set();
        /** Callback to trigger dirty marking on the font manager side */
        this._onDirty = null;
        /** Callback after _syncJsonFromYDoc (undo/redo/remote) for external resync */
        this._onAfterSync = null;
        /** Suppress recording (used during undo/redo application) */
        this._suppressRecording = false;
        /** Number of active scoped recording suppressions. */
        this._recordingSuppressionDepth = 0;
        /** Index into _changeLog marking the last entry broadcast to peers */
        this._lastBroadcastLogIndex = 0;
        /** Index into _changeLog marking the last entry emitted to local-update listeners */
        this._lastLocalUpdateLogIndex = 0;
        /** Monotonic local sequence for emitted collaboration messages */
        this._nextCollaborationMessageSequence = 1;
        /** Subscribers for same-tab history UI updates */
        this._changeLogListeners = new Set();
        /** Subscribers for the flat collaboration message inspector */
        this._collaborationLogListeners = new Set();
        /** Optional callback that can append derived operations before commit */
        this._transactionFinalizer = null;
        /** Suppress raw yDoc.on('update') broadcasting while emitting canonical diffs manually. */
        this._suppressAutomaticLocalUpdateEmission = false;
        /**
         * Compact Yjs state-vector (one clock entry per client, typically < 100 bytes)
         * captured after each local or remote Y.Doc mutation. Used to compute the
         * minimal incremental diff for outbound broadcasts without serialising the
         * entire font document.
         */
        this._lastBroadcastStateVector = new Uint8Array(0);
        this.windowId = windowId ?? windowRole.instanceId;
        this.yDoc = new Y.Doc({ gc: false });
        this.fontMap = this.yDoc.getMap('font');
        // Listen for Y.Doc updates.
        // Only broadcast updates whose origin is a known local edit origin.
        // Yjs CRDT reconciliation updates have origin=undefined and must NOT
        // be broadcast, or they create a ping-pong echo loop between windows.
        const LOCAL_EDIT_ORIGINS = new Set([
            USER_EDIT_ORIGIN,
            FONT_EDIT_ORIGIN,
            GLYPH_EDIT_ORIGIN,
            HISTORY_REPLAY_ORIGIN
        ]);
        const isLocalEditOrigin = (origin) => {
            if (typeof origin !== 'string')
                return false;
            if (LOCAL_EDIT_ORIGINS.has(origin))
                return true;
            if (origin.startsWith(LAYER_EDIT_ORIGIN_PREFIX))
                return true;
            return false;
        };
        this.yDoc.on('update', (update, origin) => {
            if (isLocalEditOrigin(origin) &&
                !this._isApplyingRemote &&
                !this._suppressAutomaticLocalUpdateEmission) {
                this._emitRawLocalUpdate(update);
            }
        });
    }
    _emitRawLocalUpdate(update) {
        if (!update.length) {
            this._lastLocalUpdateLogIndex = this._changeLog.length;
            this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
            return;
        }
        this._emitLocalUpdate(update, this._getNewChangeLogEntriesForLocalUpdate());
        this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
    }
    _emitLocalUpdate(update, changeLogEntries) {
        // Undo/redo may append a coarse control entry for history-stack state,
        // but every emitted Yjs packet must still be observed through the same
        // semantic metadata shape as the original forward edit. Unwrap control
        // rows here so worker/cache/compile/broadcast consumers never need an
        // undo-specific path.
        const emissionEntries = getEffectiveEmissionEntries(changeLogEntries);
        const collaborationMessage = createCollaborationMessageEnvelopeFromChangeLogEntries(emissionEntries, {
            localSequence: this._nextCollaborationMessageSequence++,
            source: 'change-bridge',
            windowId: this.windowId
        });
        if (collaborationMessage) {
            this._appendCollaborationLogItems([
                this._createCollaborationLogItem(collaborationMessage, update, 'local', this._deriveForwardChangesFromChangeLogEntries(emissionEntries))
            ]);
        }
        for (const cb of this._localUpdateListeners) {
            cb(update, collaborationMessage, emissionEntries);
        }
        this._yjsWorkerCallback?.(update, emissionEntries);
        for (const cb of this._committedChangeListeners) {
            cb(emissionEntries, { origin: 'local', update });
        }
    }
    _emitCanonicalLocalUpdateSince(previousStateVector) {
        // Compute only the operations added since previousStateVector — no need
        // to allocate two temporary Y.Doc instances; the live yDoc IS the current
        // state, so we just diff against the stored state vector.
        const incrementalUpdate = Y.encodeStateAsUpdate(this.yDoc, previousStateVector);
        this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
        if (incrementalUpdate.length === 0) {
            this._lastLocalUpdateLogIndex = this._changeLog.length;
            return;
        }
        this._emitLocalUpdate(incrementalUpdate, this._getNewChangeLogEntriesForLocalUpdate());
    }
    _emitCanonicalLocalUpdateFromBaseline() {
        this._emitCanonicalLocalUpdateSince(this._lastBroadcastStateVector);
    }
    getFontJsonSnapshot() {
        return this._fontJson;
    }
    /**
     * Read-only sizes for the Preferences memory breakdown.
     * Does not encode the Y.Doc (that would allocate and move Used).
     */
    getMemoryInspectionSnapshot() {
        const undoManagers = [
            this._fontUndoManager,
            ...this._undoManagers.values(),
            ...Array.from(this._layerUndoManagers.values()).map((entry) => entry.manager)
        ].filter((manager) => manager != null);
        const undoStacks = [];
        let undoStackItems = 0;
        for (const manager of undoManagers) {
            undoStacks.push(manager.undoStack, manager.redoStack);
            undoStackItems += manager.undoStack?.length ?? 0;
            undoStackItems += manager.redoStack?.length ?? 0;
        }
        return {
            fontJson: this._fontJson,
            yDocStore: this.yDoc.store,
            undoStacks,
            undoStackItems,
            changeLog: this._changeLog,
            collaborationLog: this._collaborationLog
        };
    }
    // ── Lifecycle ────────────────────────────────────────────────
    /**
     * Initialize the Y.Doc from the current babelfont JSON.
     * Call this once after a font is loaded, before steady-state incremental
     * Yjs updates begin.
     */
    initFromJson(fontJson) {
        this._fontJson = fontJson;
        this._isSyncing = true;
        this.yDoc.transact(() => {
            // Clear existing content
            this.fontMap.forEach((_v, k) => {
                this.fontMap.delete(k);
            });
            jsonToYDoc(fontJson, this.fontMap);
        }, USER_EDIT_ORIGIN);
        this._isSyncing = false;
        this._rehydrateEntireFontJsonFromYDoc();
        this._setupFontUndoManager();
        this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
    }
    /**
     * Set the font JSON reference without populating the Y.Doc.
     * Used by sync (secondary) windows that will receive the Y.Doc
     * state from a peer via applyFullState().
     */
    setFontJson(fontJson) {
        this._fontJson = fontJson;
    }
    /** Register a callback for when a remote change modifies local JSON. */
    onRemoteChange(cb) {
        this._onRemoteChange = cb;
    }
    onCollaborationLogUpdate(cb) {
        this._collaborationLogListeners.add(cb);
        cb(this.getCollaborationLog());
        return () => {
            this._collaborationLogListeners.delete(cb);
        };
    }
    /** Register a callback for local Y.Doc updates (for broadcasting). */
    onLocalUpdate(cb) {
        this._localUpdateListeners.add(cb);
    }
    /** Unregister a callback previously passed to onLocalUpdate. */
    offLocalUpdate(cb) {
        this._localUpdateListeners.delete(cb);
    }
    /** Register a callback for committed local and remote changes. */
    onCommittedChange(cb) {
        this._committedChangeListeners.add(cb);
    }
    /** Unregister a callback previously passed to onCommittedChange. */
    offCommittedChange(cb) {
        this._committedChangeListeners.delete(cb);
    }
    /** Register a callback to mark the font as dirty. */
    onDirty(cb) {
        this._onDirty = cb;
    }
    /** Register a callback for after _syncJsonFromYDoc (undo/redo/remote). */
    onAfterSync(cb) {
        this._onAfterSync = cb;
    }
    setTransactionFinalizer(cb) {
        this._transactionFinalizer = cb;
    }
    /** Clean up resources. */
    destroy() {
        for (const entry of this._layerUndoManagers.values()) {
            entry.manager.destroy();
        }
        this._layerUndoManagers.clear();
        for (const um of this._undoManagers.values()) {
            um.destroy();
        }
        this._undoManagers.clear();
        this._fontUndoManager?.destroy();
        this._fontUndoManager = null;
        this._undoHistoryStacks.clear();
        this.yDoc.destroy();
        this._fontJson = null;
        this._changeLog = [];
        this._collaborationLog = [];
        this._onRemoteChange = null;
        this._localUpdateListeners.clear();
        this._committedChangeListeners.clear();
        this._onDirty = null;
        this._onAfterSync = null;
        this._changeLogListeners.clear();
        this._collaborationLogListeners.clear();
        this._transactionFinalizer = null;
        this._txStartTimeMs = null;
        this._lastBroadcastStateVector = new Uint8Array(0);
    }
    onChangeLogUpdate(cb) {
        this._changeLogListeners.add(cb);
        cb(this.getChangeLog());
        return () => {
            this._changeLogListeners.delete(cb);
        };
    }
    // ── Change recording ─────────────────────────────────────────
    /**
     * Record a property change. Called by model setters.
     *
     * @param path   Array path from font root, e.g. ["glyphs","A","layers","uuid","width"]
     * @param prop   Terminal property name, e.g. "width"
     * @param oldVal Previous value
     * @param newVal New value
     */
    recordChange(path, prop, oldVal, newVal) {
        if (this._suppressRecording || this._isSyncing)
            return;
        const fullPath = [...path, prop];
        this._queueOrCommitOperations([
            {
                op: 'set',
                path: fullPath,
                oldValue: cloneHistoryValue(oldVal),
                newValue: cloneHistoryValue(newVal)
            }
        ]);
    }
    /**
     * Record an add operation (new glyph, layer, shape, etc.).
     * Glyph-root adds also sync `glyphOrder` from the model array when the
     * glyph is already spliced there (addGlyph / duplicate / paste), so
     * `_syncAllGlyphsFromYDoc` does not append the new name at the end.
     */
    recordAdd(path, value) {
        if (this._suppressRecording || this._isSyncing)
            return;
        const isObjectValue = !!value && typeof value === 'object' && !Array.isArray(value);
        const isLayerRoot = path.length === 4 && path[0] === 'glyphs' && path[2] === 'layers';
        const operations = [
            {
                op: 'add',
                path,
                oldValue: undefined,
                newValue: cloneHistoryValue(value),
                applyMode: isObjectValue && this._isGlyphRootPath(path)
                    ? 'glyph-snapshot'
                    : isObjectValue && isLayerRoot
                        ? 'layer-snapshot'
                        : 'default'
            }
        ];
        if (this._isGlyphRootPath(path)) {
            const glyphOrderOp = this._createModelGlyphOrderSyncOperation(String(path[1]), 'add');
            if (glyphOrderOp) {
                operations.push(glyphOrderOp);
            }
        }
        this._queueOrCommitOperations(operations);
    }
    /**
     * Record a remove operation.
     */
    recordRemove(path, oldValue) {
        if (this._suppressRecording || this._isSyncing)
            return;
        this._queueOrCommitOperations([
            {
                op: 'remove',
                path,
                oldValue: cloneHistoryValue(oldValue),
                newValue: undefined
            }
        ]);
    }
    /**
     * True when the Y.Doc glyph map still holds `glyphName`.
     * Used to preflight renames before model mutation.
     */
    hasGlyphInYDoc(glyphName) {
        return this._readNormalizedGlyphSnapshotFromYDoc(glyphName) != null;
    }
    /**
     * Rename glyph-map keys while preserving their order and storage encoding.
     * Removes every old key before inserting new keys so swaps stay collision-free.
     */
    renameGlyphs(renames, label) {
        if (!renames.length || this._suppressRecording || this._isSyncing) {
            return;
        }
        const prepared = renames.map(({ oldName, newName, glyph }) => {
            const oldGlyph = this._readNormalizedGlyphSnapshotFromYDoc(oldName);
            if (!oldGlyph) {
                throw new Error(`Cannot rename glyph "${oldName}": absent from Y.Doc`);
            }
            return { oldName, newName, glyph, oldGlyph };
        });
        const fontJson = this._fontJson;
        const oldOrder = Array.isArray(fontJson?.glyphs)
            ? fontJson.glyphs.map((glyph) => glyph.name || '')
            : [];
        const renameMap = new Map(prepared.map(({ oldName, newName }) => [oldName, newName]));
        const nextOrder = oldOrder.map((name) => renameMap.get(name) || name);
        const operations = [];
        // Phase 1: drop every old key (enables A↔B swaps).
        for (const { oldName, newName, oldGlyph } of prepared) {
            operations.push({
                op: 'remove',
                path: ['glyphs', oldName],
                oldValue: oldGlyph,
                newValue: undefined,
                glyphRenames: [{ oldName, newName }]
            });
        }
        // Phase 2: insert every new key from the prepared snapshots.
        for (const { newName, glyph } of prepared) {
            operations.push({
                op: 'add',
                path: ['glyphs', newName],
                oldValue: undefined,
                // New glyph snapshots need shape ids for Rust's Shape enum.
                // The normal patch encoder strips those ids because existing
                // shape patches address them structurally.
                newValue: this._prepareStorageValue(glyph),
                applyMode: 'glyph-snapshot'
            });
        }
        operations.push({
            op: 'set',
            path: ['glyphOrder'],
            oldValue: oldOrder,
            newValue: nextOrder
        });
        this._queueOrCommitOperations(operations, label);
    }
    applySyntheticChangeSet(label, operations) {
        if (!operations.length ||
            !this._fontJson ||
            this._suppressRecording ||
            this._isSyncing) {
            return;
        }
        if (!operations.some((operation) => operation.path.length > 0)) {
            return;
        }
        this._queueOrCommitOperations(operations.map((operation) => {
            const isObjectValue = !!operation.newValue &&
                typeof operation.newValue === 'object' &&
                !Array.isArray(operation.newValue);
            const isGlyphRoot = this._isGlyphRootPath(operation.path);
            const isLayerRoot = operation.path.length === 4 &&
                operation.path[0] === 'glyphs' &&
                operation.path[2] === 'layers';
            return {
                op: operation.op,
                path: operation.path,
                oldValue: cloneHistoryValue(operation.oldValue),
                newValue: cloneHistoryValue(operation.newValue),
                editSource: operation.editSource ?? null,
                compileChangeSource: operation.compileChangeSource ?? null,
                compileEditType: operation.compileEditType ?? null,
                visualAnchorSide: operation.visualAnchorSide ?? null,
                workerReplayTargets: normalizeWorkerReplayTargets(operation.workerReplayTargets),
                applyMode: isObjectValue && isGlyphRoot
                    ? 'glyph-snapshot'
                    : isObjectValue && isLayerRoot
                        ? 'layer-snapshot'
                        : 'default'
            };
        }), label);
    }
    /**
     * Commit an externally edited source snapshot through the regular Yjs
     * change path. The caller supplies the state vector captured before the
     * asynchronous source read so a concurrent local or remote edit cannot be
     * overwritten by the disk snapshot.
     */
    applyExternalSourceReload(fontSnapshot, expectedStateVector) {
        if (!this._fontJson ||
            this._suppressRecording ||
            this._isSyncing ||
            this._isApplyingRemote ||
            this._txDepth > 0 ||
            !stateVectorsEqual(expectedStateVector, Y.encodeStateVector(this.yDoc))) {
            return { status: 'stale', commit: null };
        }
        const previousSnapshot = this._normalizeExternalSourceReloadSnapshot(this._fontJson);
        const normalizedPreviousSnapshot = this._normalizeFontSnapshot(previousSnapshot, previousSnapshot);
        const storageFontSnapshot = this._normalizeExternalSourceReloadSnapshot(fontSnapshot);
        const nextSnapshot = this._normalizeFontSnapshot(storageFontSnapshot, previousSnapshot);
        this._adoptIndexedFontLayerIds(normalizedPreviousSnapshot, nextSnapshot);
        if (this._isDeepEqual(normalizedPreviousSnapshot, nextSnapshot)) {
            return { status: 'noop', commit: null };
        }
        const commit = this._queueOrCommitOperations(diffFontDataToPatchPairs(normalizedPreviousSnapshot, nextSnapshot).map(({ forward, inverse }) => {
            const path = forward.path;
            const newValue = forward.op === 'remove' ? undefined : forward.value;
            const oldValue = inverse.op === 'remove' ? undefined : inverse.value;
            const isObjectValue = !!newValue &&
                typeof newValue === 'object' &&
                !Array.isArray(newValue);
            const isGlyphRoot = this._isGlyphRootPath(path);
            const isLayerRoot = path.length === 4 &&
                path[0] === 'glyphs' &&
                path[2] === 'layers';
            return {
                op: forward.op === 'replace'
                    ? 'set'
                    : forward.op,
                path,
                oldValue,
                newValue,
                applyMode: isObjectValue && isGlyphRoot
                    ? 'glyph-snapshot'
                    : isObjectValue && isLayerRoot
                        ? 'layer-snapshot'
                        : 'default'
            };
        }), 'Reload external source', true);
        if (commit) {
            this._syncRemoteJsonFromYDoc(commit.changeLogEntries);
            this._onAfterSync?.();
        }
        return {
            status: commit ? 'committed' : 'noop',
            commit
        };
    }
    _normalizeExternalSourceReloadSnapshot(value) {
        const normalizeShapes = (candidate, isShapeEntry = false) => {
            if (Array.isArray(candidate)) {
                return candidate.map((item) => normalizeShapes(item, isShapeEntry));
            }
            if (!candidate || typeof candidate !== 'object') {
                return candidate;
            }
            const record = candidate;
            const normalizedRecord = isShapeEntry
                ? normalizeValueForYDocWrite(record)
                : { ...record };
            if (!normalizedRecord ||
                typeof normalizedRecord !== 'object' ||
                Array.isArray(normalizedRecord)) {
                throw new TypeError('Expected a normalized shape record for external reload.');
            }
            return Object.fromEntries(Object.entries(normalizedRecord).map(([key, item]) => [
                key,
                normalizeShapes(item, key === 'shapes')
            ]));
        };
        return this._prepareStorageValue(normalizeShapes(value));
    }
    // ── Transactions ─────────────────────────────────────────────
    /**
     * Start a named batch transaction.
     * Nested calls increment a depth counter; only the outermost commits.
     */
    beginTransaction(label, historyTarget, historyMetadata) {
        this._txDepth++;
        if (this._txDepth === 1) {
            this._txLabel = label;
            this._txId = this._nextTxId++;
            this._txStartTimeMs = performance.now();
            this._txHistoryItemId =
                historyMetadata?.historyItemId ?? this._createHistoryItemId();
            this._txPromptGroupId = historyMetadata?.promptGroupId ?? null;
            this._txHistorySummary = historyMetadata?.historySummary ?? null;
            this._txHistoryTarget = historyTarget ?? null;
            // Capture a compact state-vector (< 100 bytes) rather than the
            // full font serialization so the fallback canonical-diff path in
            // _emitCanonicalLocalUpdateSince stays cheap.
            this._txStartStateVector = Y.encodeStateVector(this.yDoc);
        }
    }
    /** Update presentation metadata for the current outermost transaction. */
    updateTransactionMetadata(promptGroupId, label, historySummary) {
        if (this._txDepth <= 0 || this._txPromptGroupId !== promptGroupId) {
            return false;
        }
        this._txLabel = label;
        this._txHistorySummary = historySummary;
        return true;
    }
    /**
     * End the current batch transaction.
     */
    endTransaction() {
        if (this._txDepth <= 0)
            return null;
        this._txDepth--;
        let commitResult = null;
        if (this._txDepth === 0) {
            if (this._txBufferedOperations.length) {
                commitResult = this._commitOperations(this._txBufferedOperations, this._txLabel, this._txId, this._txHistoryItemId, this._txHistoryTarget, this._txPromptGroupId, this._txHistorySummary);
            }
            this._txBufferedOperations = [];
            this._txLabel = null;
            this._txId = null;
            this._txStartTimeMs = null;
            this._txHistoryItemId = null;
            this._txPromptGroupId = null;
            this._txHistorySummary = null;
            this._txHistoryTarget = null;
            this._txStartStateVector = null;
        }
        return commitResult;
    }
    /** Whether a transaction is currently open. */
    get inTransaction() {
        return this._txDepth > 0;
    }
    setRecordingSuppressed(suppressed) {
        this._recordingSuppressionDepth = suppressed ? 1 : 0;
        this._suppressRecording = suppressed;
    }
    /**
     * Suppress model recording until the returned release function is called.
     * Each release is idempotent so overlapping lifecycle cleanup is safe.
     */
    beginRecordingSuppression() {
        this._recordingSuppressionDepth += 1;
        this._suppressRecording = true;
        let released = false;
        return () => {
            if (released) {
                return;
            }
            released = true;
            this._recordingSuppressionDepth = Math.max(0, this._recordingSuppressionDepth - 1);
            this._suppressRecording = this._recordingSuppressionDepth > 0;
        };
    }
    runWithoutRecording(fn) {
        const wasSuppressed = this._suppressRecording;
        this._suppressRecording = true;
        try {
            return fn();
        }
        finally {
            this._suppressRecording = wasSuppressed;
        }
    }
    // ── Bulk sync (after drag / external mutation) ───────────────
    /**
     * Sync a glyph's current JSON data into the Y.Doc.
     *
     * Call this after operations that mutate `babelfontData` directly
     * (e.g. outline-editor drag) instead of going through model setters.
     * Updates the existing Y.Map in-place so per-glyph UndoManagers
     * keep their scope reference.
     */
    syncGlyphFromJson(glyphName, label, oldValue, newValue, layerId, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType, previousGlyphSnapshot) {
        this.syncGlyphsFromJson([glyphName], label, oldValue, newValue, layerId, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType, previousGlyphSnapshot
            ? { [glyphName]: previousGlyphSnapshot }
            : undefined);
    }
    /**
     * Sync multiple changed layers into Y.Doc in one transaction.
     * Each target stays on the layer fast path so linked windows only
     * receive the minimum changed layer snapshots.
     */
    syncLayersFromJson(layerTargets, label, oldValue, newValue, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType) {
        if (!this._fontJson || this._suppressRecording || this._isSyncing) {
            return;
        }
        const uniqueTargets = normalizeWorkerReplayTargets(layerTargets);
        if (!uniqueTargets.length) {
            return;
        }
        if (uniqueTargets.length === 1) {
            const [target] = uniqueTargets;
            this._trySyncSingleLayer(target.glyphName, target.layerId, label, oldValue, newValue, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType, true);
            return;
        }
        const glyphs = this._fontJson.glyphs;
        if (!Array.isArray(glyphs)) {
            return;
        }
        const glyphsMap = this.fontMap.get('glyphs');
        if (!glyphsMap) {
            return;
        }
        const operations = [];
        for (const { glyphName, layerId } of uniqueTargets) {
            const glyphJson = glyphs.find((g) => g.name === glyphName);
            if (!glyphJson) {
                continue;
            }
            const glyphMap = glyphsMap.get(glyphName);
            if (!glyphMap) {
                continue;
            }
            const layersMap = glyphMap.get('layers');
            if (!layersMap) {
                continue;
            }
            const glyphLayers = (glyphJson.layers ?? []);
            const layerJson = glyphLayers.find((layer) => layer.id === layerId);
            if (!layerJson) {
                continue;
            }
            const storageLayerJson = this._prepareLayerSnapshotForHistory(layerId, layerJson, layersMap.get(layerId)
                ? fromYType(layersMap.get(layerId))
                : null);
            operations.push(...this._buildLayerSyncOperations(glyphName, layerId, layersMap, storageLayerJson, label, oldValue, newValue, {
                editSource: editSource ?? compileChangeSource ?? null,
                compileChangeSource,
                compileEditType,
                visualAnchorSide,
                workerReplayTargets,
                preserveOmittedFields: true
            }));
        }
        if (!operations.length) {
            return;
        }
        this._queueOrCommitOperations(operations, label);
        console.log(`Layer sync committed for ${uniqueTargets
            .map((target) => `${target.glyphName}/${target.layerId}`)
            .join(', ')} (${label}) [batched fast path]`);
    }
    /**
     * Sync explicit changed-layer snapshots into Y.Doc in one transaction.
     * Use this when the caller already has committed next-layer JSON and the
     * bridge's shared `_fontJson` reference may alias mutable editor storage.
     */
    syncLayerSnapshotsFromJson(layerTargets, label, oldValue, newValue, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType) {
        if (this._suppressRecording || this._isSyncing) {
            return;
        }
        const uniqueTargets = [];
        const seenTargets = new Set();
        for (const target of layerTargets) {
            if (!target.glyphName || !target.layerId || !target.layerJson) {
                continue;
            }
            const key = `${target.glyphName}@@${target.layerId}`;
            if (seenTargets.has(key)) {
                continue;
            }
            seenTargets.add(key);
            uniqueTargets.push(target);
        }
        if (!uniqueTargets.length) {
            return;
        }
        const glyphsMap = this.fontMap.get('glyphs');
        if (!glyphsMap) {
            return;
        }
        const operations = [];
        for (const { glyphName, layerId, layerJson, authoritativeOptionalLayerFields } of uniqueTargets) {
            const glyphMap = glyphsMap.get(glyphName);
            if (!glyphMap) {
                continue;
            }
            const layersMap = glyphMap.get('layers');
            if (!layersMap) {
                continue;
            }
            const yLayerMap = layersMap.get(layerId);
            const yLayerJson = yLayerMap ? fromYType(yLayerMap) : null;
            const storageLayerJson = this._prepareLayerSnapshotForHistory(layerId, layerJson, yLayerJson);
            if (authoritativeOptionalLayerFields !== undefined) {
                if (yLayerJson &&
                    typeof yLayerJson === 'object' &&
                    !Array.isArray(yLayerJson)) {
                    const existingLayer = yLayerJson;
                    const authoritativeFields = new Set(authoritativeOptionalLayerFields);
                    for (const key of [
                        'anchors',
                        'guides',
                        'format_specific'
                    ]) {
                        if (authoritativeFields.has(key)) {
                            continue;
                        }
                        if (Object.prototype.hasOwnProperty.call(existingLayer, key)) {
                            storageLayerJson[key] = cloneHistoryValue(existingLayer[key]);
                        }
                        else {
                            delete storageLayerJson[key];
                        }
                    }
                }
            }
            operations.push(...this._buildLayerSyncOperations(glyphName, layerId, layersMap, storageLayerJson, label, oldValue, newValue, {
                editSource: editSource ?? compileChangeSource ?? null,
                compileChangeSource,
                compileEditType,
                visualAnchorSide,
                workerReplayTargets
            }));
        }
        if (!operations.length) {
            this._emitMetadataOnlyLayerSnapshotUpdate(uniqueTargets, label, oldValue, newValue, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType);
            return;
        }
        const wasInTransaction = this._txDepth > 0;
        const commitResult = this._queueOrCommitOperations(operations, label);
        // Only reload layers that this packet actually wrote into Yjs.
        // workerReplayTargets are intentionally wider (invalidate-only
        // dependents for compile/cache). Reloading those from Yjs would
        // clobber live-already-recomposed model state with stale document
        // data when those dependents were not part of the mutation set.
        if (!wasInTransaction && operations.length) {
            const writtenLayerTargets = normalizeWorkerReplayTargets(operations.map((operation) => {
                const applyPath = operation.applyPath ?? operation.path;
                return {
                    glyphName: deriveGlyphName(applyPath) || '',
                    layerId: deriveLayerId(applyPath) || ''
                };
            }));
            if (writtenLayerTargets.length) {
                this._syncPatchedLayerTargetsFromYDoc(writtenLayerTargets);
            }
        }
        if (!wasInTransaction && !commitResult) {
            this._emitMetadataOnlyLayerSnapshotUpdate(uniqueTargets, label, oldValue, newValue, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType);
            return;
        }
        console.log(`Layer snapshot sync committed for ${uniqueTargets
            .map((target) => `${target.glyphName}/${target.layerId}`)
            .join(', ')} (${label}) [batched fast path]`);
    }
    _emitMetadataOnlyLayerSnapshotUpdate(uniqueTargets, label, oldValue, newValue, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType) {
        const normalizedReplayTargets = normalizeWorkerReplayTargets(workerReplayTargets);
        if (!normalizedReplayTargets.length) {
            return;
        }
        const update = Y.encodeStateAsUpdate(this.yDoc, Y.encodeStateVector(this.yDoc));
        const timestamp = Date.now();
        const historyItemId = this._createHistoryItemId();
        const changeLogEntries = uniqueTargets.map((target) => createLogEntry({
            timestamp,
            windowId: this.windowId,
            windowRoleLabel: this._getWindowRoleLabel(),
            historyItemId,
            historyAction: 'change',
            transactionLabel: label,
            transactionId: null,
            op: 'set',
            path: `glyphs.${target.glyphName}:layers.${target.layerId}`,
            oldValue: oldValue ?? target.glyphName,
            newValue: newValue ?? label,
            replayOldValue: undefined,
            replayNewValue: undefined,
            editSource: editSource ?? compileChangeSource ?? null,
            compileChangeSource: compileChangeSource ?? null,
            compileEditType: compileEditType ?? null,
            visualAnchorSide: visualAnchorSide ?? null,
            workerReplayTargets: normalizedReplayTargets
        }));
        this._lastLocalUpdateLogIndex = this._changeLog.length;
        this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
        this._emitLocalUpdate(update, changeLogEntries);
    }
    _buildLayerSyncOperations(glyphName, layerId, layersMap, storageLayerJson, label, oldValue, newValue, metadata) {
        const yLayerMap = layersMap.get(layerId);
        const yLayerJson = yLayerMap ? fromYType(yLayerMap) : null;
        if (!yLayerJson) {
            return [
                {
                    op: 'set',
                    path: ['glyphs', glyphName, 'layers', layerId],
                    oldValue: null,
                    newValue: cloneHistoryValue(storageLayerJson),
                    editSource: metadata.editSource,
                    compileChangeSource: metadata.compileChangeSource,
                    compileEditType: metadata.compileEditType,
                    visualAnchorSide: metadata.visualAnchorSide,
                    workerReplayTargets: metadata.workerReplayTargets,
                    originatingGlyphName: glyphName,
                    originatingLayerId: layerId,
                    applyPath: ['glyphs', glyphName, 'layers', layerId],
                    applyNewValue: storageLayerJson,
                    applyMode: 'layer-snapshot'
                }
            ];
        }
        const previousLayer = yLayerJson;
        if (!Object.prototype.hasOwnProperty.call(storageLayerJson, 'name') &&
            Object.prototype.hasOwnProperty.call(previousLayer, 'name')) {
            storageLayerJson.name = cloneHistoryValue(previousLayer.name);
        }
        if (this._shouldUseGranularSingleLayerSync(glyphName, layerId, previousLayer, storageLayerJson)) {
            this._adoptIndexedLayerIds(previousLayer, storageLayerJson);
            return this._buildGranularLayerSyncOperations(glyphName, layerId, previousLayer, storageLayerJson, metadata);
        }
        const sparseLayerDelta = this._buildSparseLayerDelta(previousLayer, storageLayerJson, layerId);
        if (!sparseLayerDelta) {
            return [];
        }
        return [
            {
                op: 'set',
                path: ['glyphs', glyphName, 'layers', layerId],
                oldValue: oldValue ?? glyphName,
                newValue: newValue ?? label,
                editSource: metadata.editSource,
                compileChangeSource: metadata.compileChangeSource,
                compileEditType: metadata.compileEditType,
                visualAnchorSide: metadata.visualAnchorSide,
                workerReplayTargets: metadata.workerReplayTargets,
                originatingGlyphName: glyphName,
                originatingLayerId: layerId,
                applyPath: ['glyphs', glyphName, 'layers', layerId],
                applyOldValue: sparseLayerDelta.oldValues,
                applyNewValue: sparseLayerDelta.delta,
                applyMode: 'layer-snapshot'
            }
        ];
    }
    _buildGranularLayerSyncOperations(glyphName, layerId, previousLayer, nextLayer, metadata) {
        const basePath = [
            'glyphs',
            glyphName,
            'layers',
            layerId
        ];
        return this._buildGranularValueSyncOperations(basePath, previousLayer, nextLayer, metadata);
    }
    _buildGranularValueSyncOperations(path, previousValue, nextValue, metadata) {
        if (this._isDeepEqual(previousValue, nextValue)) {
            return [];
        }
        const lastSegment = path[path.length - 1];
        if (typeof lastSegment === 'string' &&
            GRANULAR_LAYER_ARRAY_KEYS.has(lastSegment) &&
            Array.isArray(previousValue) &&
            Array.isArray(nextValue)) {
            return this._buildGranularIndexedArraySyncOperations(path, lastSegment, previousValue, nextValue, metadata);
        }
        // Paths have no persisted IDs, so numeric child paths cannot safely
        // replay a contour insertion, deletion, or structural replacement.
        // Keep coordinate-only node edits granular.
        if (lastSegment === 'shapes' &&
            Array.isArray(previousValue) &&
            Array.isArray(nextValue) &&
            this._haveShapeStructuresChanged(previousValue, nextValue)) {
            return [
                this._createGranularLayerOperation('set', path, previousValue, nextValue, metadata)
            ];
        }
        if (previousValue &&
            nextValue &&
            typeof previousValue === 'object' &&
            typeof nextValue === 'object' &&
            !Array.isArray(previousValue) &&
            !Array.isArray(nextValue)) {
            const previousRecord = previousValue;
            const nextRecord = nextValue;
            const keys = new Set([
                ...Object.keys(previousRecord),
                ...Object.keys(nextRecord)
            ]);
            const operations = [];
            for (const key of keys) {
                if (key === 'id') {
                    continue;
                }
                if (metadata.preserveOmittedFields &&
                    !Object.prototype.hasOwnProperty.call(nextRecord, key)) {
                    continue;
                }
                operations.push(...this._buildGranularValueSyncOperations([...path, key], previousRecord[key], nextRecord[key], metadata));
            }
            return operations;
        }
        if (Array.isArray(previousValue) && Array.isArray(nextValue)) {
            // Unkeyed arrays have no stable member identity. A cardinality
            // change shifts numeric paths, so replay it as one array value.
            if (previousValue.length !== nextValue.length) {
                return [
                    this._createGranularLayerOperation('set', path, previousValue, nextValue, metadata)
                ];
            }
            const operations = [];
            const maxLength = Math.max(previousValue.length, nextValue.length);
            for (let index = 0; index < maxLength; index++) {
                operations.push(...this._buildGranularValueSyncOperations([...path, index], previousValue[index], nextValue[index], metadata));
            }
            return operations;
        }
        if (previousValue === undefined) {
            return [
                this._createGranularLayerOperation('add', path, undefined, nextValue, metadata)
            ];
        }
        if (nextValue === undefined) {
            return [
                this._createGranularLayerOperation('remove', path, previousValue, undefined, metadata)
            ];
        }
        return [
            this._createGranularLayerOperation('set', path, previousValue, nextValue, metadata)
        ];
    }
    _haveShapeStructuresChanged(previousShapes, nextShapes) {
        if (previousShapes.length !== nextShapes.length) {
            return true;
        }
        const shapeSignature = (shape) => {
            if (!shape || typeof shape !== 'object' || Array.isArray(shape)) {
                return typeof shape;
            }
            const record = shape;
            if (Array.isArray(record.nodes)) {
                return `path:${record.nodes.length}:${String(record.closed)}`;
            }
            if (typeof record.reference === 'string') {
                return 'component';
            }
            return 'shape';
        };
        return previousShapes.some((shape, index) => shapeSignature(shape) !== shapeSignature(nextShapes[index]));
    }
    _buildGranularIndexedArraySyncOperations(path, arrayKey, previousArray, nextArray, metadata) {
        const previousById = this._mapArrayItemsById(previousArray);
        const nextById = this._mapArrayItemsById(nextArray);
        if (!previousById || !nextById) {
            return [
                this._createGranularLayerOperation('set', path, previousArray, nextArray, metadata)
            ];
        }
        const operations = [];
        const previousIds = previousArray.map((item) => String(item.id));
        const nextIds = nextArray.map((item) => String(item.id));
        // Indexed positions are not stable when membership or order changes.
        // Replace the collection as one ordered Yjs value so undo and redo
        // restore the same IDs and order rather than replaying shifted indices.
        if (!this._isDeepEqual(previousIds, nextIds)) {
            return [
                this._createGranularLayerOperation('set', path, previousArray, nextArray, metadata)
            ];
        }
        nextIds.forEach((id, nextIndex) => {
            const nextItem = nextById.get(id);
            const previousIndex = previousIds.indexOf(id);
            operations.push(...this._buildGranularValueSyncOperations([...path, previousIndex], previousById.get(id), nextItem, metadata));
        });
        return operations;
    }
    _createGranularLayerOperation(op, path, oldValue, newValue, metadata) {
        const originatingGlyphName = path[0] === 'glyphs' && typeof path[1] === 'string'
            ? path[1]
            : null;
        const originatingLayerId = path[2] === 'layers' && typeof path[3] === 'string'
            ? path[3]
            : null;
        return {
            op,
            path,
            oldValue: cloneHistoryValue(oldValue),
            newValue: cloneHistoryValue(newValue),
            editSource: metadata.editSource ?? null,
            compileChangeSource: metadata.compileChangeSource ?? null,
            compileEditType: metadata.compileEditType ?? null,
            visualAnchorSide: metadata.visualAnchorSide ?? null,
            workerReplayTargets: metadata.workerReplayTargets,
            originatingGlyphName,
            originatingLayerId
        };
    }
    _mapArrayItemsById(items) {
        const map = new Map();
        for (const item of items) {
            if (!item || typeof item !== 'object' || Array.isArray(item)) {
                return null;
            }
            const id = item.id;
            if (typeof id !== 'string' || !id) {
                return null;
            }
            map.set(id, item);
        }
        return map;
    }
    _adoptIndexedLayerIds(previousLayer, nextLayer, replaceExistingIds = false) {
        this._adoptIndexedArrayIds(previousLayer.anchors, nextLayer.anchors, replaceExistingIds);
        this._adoptIndexedArrayIds(previousLayer.anchors, nextLayer.anchors, replaceExistingIds);
        this._adoptIndexedArrayIds(previousLayer.guides, nextLayer.guides, replaceExistingIds);
    }
    _adoptIndexedFontLayerIds(previousSnapshot, nextSnapshot) {
        const asRecords = (value) => Array.isArray(value)
            ? value.filter((item) => !!item &&
                typeof item === 'object' &&
                !Array.isArray(item))
            : [];
        const previousMasters = asRecords(previousSnapshot?.masters);
        const nextMasters = asRecords(nextSnapshot?.masters);
        const previousMastersById = new Map(previousMasters.map((master) => [String(master.id || ''), master]));
        for (const nextMaster of nextMasters) {
            const previousMaster = previousMastersById.get(String(nextMaster.id || ''));
            if (previousMaster) {
                if (!Object.prototype.hasOwnProperty.call(previousMaster, 'kerning_rtl') &&
                    nextMaster.kerning_rtl &&
                    typeof nextMaster.kerning_rtl === 'object' &&
                    !Array.isArray(nextMaster.kerning_rtl) &&
                    Object.keys(nextMaster.kerning_rtl).length === 0) {
                    delete nextMaster.kerning_rtl;
                }
                this._adoptIndexedArrayIds(previousMaster.guides, nextMaster.guides, true);
            }
        }
        const previousGlyphs = this._coerceFontGlyphSnapshots(previousSnapshot?.glyphs);
        const nextGlyphs = this._coerceFontGlyphSnapshots(nextSnapshot?.glyphs);
        const previousGlyphsByName = new Map(previousGlyphs.map((glyph) => [String(glyph.name || ''), glyph]));
        for (const nextGlyph of nextGlyphs) {
            const previousGlyph = previousGlyphsByName.get(String(nextGlyph.name || ''));
            if (!previousGlyph) {
                continue;
            }
            const previousLayersById = new Map(this._coerceGlyphLayerSnapshots(previousGlyph.layers).map((layer) => [String(layer.id || ''), layer]));
            for (const nextLayer of this._coerceGlyphLayerSnapshots(nextGlyph.layers)) {
                const previousLayer = previousLayersById.get(String(nextLayer.id || ''));
                if (previousLayer) {
                    this._adoptIndexedLayerIds(previousLayer, nextLayer, true);
                }
            }
        }
    }
    _adoptIndexedArrayIds(previousValue, nextValue, replaceExistingIds = false) {
        if (!Array.isArray(previousValue) || !Array.isArray(nextValue)) {
            return;
        }
        nextValue.forEach((nextItem, index) => {
            const previousItem = previousValue[index];
            if (!nextItem ||
                typeof nextItem !== 'object' ||
                Array.isArray(nextItem) ||
                !previousItem ||
                typeof previousItem !== 'object' ||
                Array.isArray(previousItem)) {
                return;
            }
            const nextRecord = nextItem;
            const previousRecord = previousItem;
            if ((replaceExistingIds || !nextRecord.id) &&
                typeof previousRecord.id === 'string') {
                nextRecord.id = previousRecord.id;
            }
        });
    }
    _isCompleteLayerSnapshot(nextLayer) {
        return (Object.prototype.hasOwnProperty.call(nextLayer, 'width') &&
            Object.prototype.hasOwnProperty.call(nextLayer, 'master') &&
            Object.prototype.hasOwnProperty.call(nextLayer, 'shapes'));
    }
    _shouldUseGranularSingleLayerSync(glyphName, layerId, previousLayer, nextLayer) {
        if (this._hasBufferedLayerRootOperation(glyphName, layerId)) {
            return false;
        }
        // Partial layer fragments must stay on the sparse layer-delta path so
        // omitted fields remain untouched. Granular sync is only safe for
        // complete layer snapshots that carry the layer's core fields.
        if (!this._isCompleteLayerSnapshot(nextLayer)) {
            return false;
        }
        return true;
    }
    _hasBufferedLayerRootOperation(glyphName, layerId) {
        return this._txBufferedOperations.some((operation) => {
            if (operation.path.length !== 4) {
                return false;
            }
            return (operation.path[0] === 'glyphs' &&
                operation.path[1] === glyphName &&
                operation.path[2] === 'layers' &&
                operation.path[3] === layerId);
        });
    }
    _buildSparseLayerDelta(previousLayer, nextLayer, layerId) {
        const delta = { id: layerId };
        const oldValues = {};
        for (const [key, value] of Object.entries(nextLayer)) {
            const oldValue = previousLayer?.[key];
            if (this._isDeepEqual(value, oldValue)) {
                continue;
            }
            delta[key] = value;
            if (oldValue !== undefined) {
                oldValues[key] = oldValue;
            }
        }
        if (Object.keys(delta).length <= 1) {
            return null;
        }
        return { delta, oldValues };
    }
    /**
     * Sync multiple glyph JSON payloads into Y.Doc in one transaction.
     * This keeps paired root/component edits aligned as a single undo step.
     */
    syncGlyphsFromJson(glyphNames, label, oldValue, newValue, layerId, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType, previousGlyphSnapshots) {
        if (!this._fontJson || this._suppressRecording || this._isSyncing)
            return;
        const uniqueGlyphNames = Array.from(new Set(glyphNames.filter((name) => typeof name === 'string')));
        if (!uniqueGlyphNames.length) {
            return;
        }
        // Fast path: single glyph + known layer → compare and sync only
        // the affected layer, avoiding full-glyph JSON reconstruction,
        // deep-equality checks, and cloning.
        if (uniqueGlyphNames.length === 1 && layerId) {
            if (this._trySyncSingleLayer(uniqueGlyphNames[0], layerId, label, oldValue, newValue, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType)) {
                return;
            }
        }
        const glyphs = this._fontJson.glyphs;
        if (!Array.isArray(glyphs))
            return;
        const glyphsMap = this.fontMap.get('glyphs');
        if (!glyphsMap)
            return;
        const targets = [];
        for (const glyphName of uniqueGlyphNames) {
            const glyphJson = glyphs.find((g) => g.name === glyphName);
            if (!glyphJson) {
                continue;
            }
            const glyphMap = glyphsMap.get(glyphName);
            if (!glyphMap) {
                continue;
            }
            const yGlyphJson = this._readNormalizedGlyphSnapshotFromYDoc(glyphName, glyphJson);
            if (!yGlyphJson) {
                continue;
            }
            const storageGlyphJson = this._prepareStorageValue(glyphJson);
            if (this._isDeepEqual(yGlyphJson, storageGlyphJson)) {
                continue;
            }
            const previousGlyphJson = previousGlyphSnapshots?.[glyphName]
                ? cloneHistoryValue(previousGlyphSnapshots[glyphName])
                : cloneHistoryValue(yGlyphJson);
            targets.push({
                glyphName,
                previousGlyphJson,
                glyphJson: storageGlyphJson
            });
        }
        if (!targets.length) {
            return;
        }
        const undoScope = this._deriveBulkUndoScope(targets, layerId ?? null);
        this._queueOrCommitOperations(targets.map((target) => {
            const glyphLayers = (target.glyphJson.layers ?? []);
            const previousGlyphLayers = Array.isArray(target.previousGlyphJson.layers)
                ? target.previousGlyphJson.layers
                : [];
            const glyphSnapshot = this._normalizeGlyphSnapshot(target.glyphJson, target.previousGlyphJson);
            const previousGlyphSnapshot = this._normalizeGlyphSnapshot(target.previousGlyphJson);
            const layerSnapshot = layerId
                ? this._normalizeLayerSnapshot(layerId, glyphLayers.find((layer) => layer.id === layerId), previousGlyphLayers.find((layer) => layer.id === layerId))
                : undefined;
            const previousLayerSnapshot = layerId
                ? previousGlyphLayers.find((layer) => layer.id === layerId)
                : undefined;
            const isLayerScope = undoScope === 'layer' && layerId;
            const originatingLayerId = layerId ??
                (Array.isArray(target.glyphJson.layers)
                    ? String(target.glyphJson.layers.find((layer) => layer?.id)?.id ?? '') || null
                    : null);
            return {
                op: 'set',
                path: isLayerScope
                    ? ['glyphs', target.glyphName, 'layers', layerId]
                    : ['glyphs', target.glyphName],
                oldValue: undoScope === 'font'
                    ? target.previousGlyphJson
                    : cloneHistoryValue(oldValue ?? target.glyphName),
                newValue: undoScope === 'font'
                    ? cloneHistoryValue(target.glyphJson)
                    : cloneHistoryValue(newValue ?? label),
                editSource: editSource ?? compileChangeSource ?? null,
                compileChangeSource,
                compileEditType,
                visualAnchorSide,
                workerReplayTargets,
                originatingGlyphName: target.glyphName,
                originatingLayerId,
                applyPath: isLayerScope
                    ? ['glyphs', target.glyphName, 'layers', layerId]
                    : ['glyphs', target.glyphName],
                applyOldValue: isLayerScope
                    ? previousLayerSnapshot
                    : previousGlyphSnapshot,
                applyNewValue: isLayerScope ? layerSnapshot : glyphSnapshot,
                applyMode: isLayerScope
                    ? 'layer-snapshot'
                    : 'glyph-snapshot'
            };
        }), label);
        console.log(`Glyph sync committed for ${targets.map((target) => target.glyphName).join(', ')} (${label})`);
    }
    /**
     * Instead of reconstructing the entire glyph from the Y.Doc,
     * deep-comparing the whole glyph, and cloning it multiple times,
     * this only touches the one affected layer — dramatically reducing
     * JSON serialization overhead for point-move operations.
     *
     * Returns true if the fast path handled the sync (even if no
     * changes were found), false if the caller should fall back to
     * the full glyph path.
     */
    _trySyncSingleLayer(glyphName, layerId, label, oldValue, newValue, visualAnchorSide, workerReplayTargets, editSource, compileChangeSource, compileEditType, preserveOmittedFields = false) {
        const glyphs = this._fontJson.glyphs;
        if (!Array.isArray(glyphs))
            return false;
        const glyphJson = glyphs.find((g) => g.name === glyphName);
        if (!glyphJson)
            return false;
        const glyphsMap = this.fontMap.get('glyphs');
        if (!glyphsMap)
            return false;
        const glyphMap = glyphsMap.get(glyphName);
        if (!glyphMap)
            return false;
        const layersMap = glyphMap.get('layers');
        if (!layersMap)
            return false;
        // Find the layer in the in-memory model
        const glyphLayers = (glyphJson.layers ?? []);
        const layerJson = glyphLayers.find((l) => l.id === layerId);
        if (!layerJson)
            return false;
        const existingYLayer = layersMap.get(layerId);
        const storageLayerJson = this._prepareLayerSnapshotForHistory(layerId, layerJson, existingYLayer instanceof Y.Map ? fromYType(existingYLayer) : null);
        const operations = this._buildLayerSyncOperations(glyphName, layerId, layersMap, storageLayerJson, label, oldValue, newValue, {
            editSource: editSource ?? compileChangeSource ?? null,
            compileChangeSource,
            compileEditType,
            visualAnchorSide,
            workerReplayTargets,
            preserveOmittedFields
        });
        if (!operations.length) {
            return false;
        }
        this._queueOrCommitOperations(operations, label);
        console.log(`Glyph sync committed for ${glyphName} layer ${layerId} (${label}) [fast path]`);
        return true;
    }
    // ── Undo / Redo ──────────────────────────────────────────────
    /**
     * Undo the last change for a specific glyph, or font-level if no
     * glyph name is given.
     *
     * YJS_ONLY: Both the native UndoManager branch and history-replay branch
     * emit a canonical binary Yjs update into the standard post-commit funnel.
     */
    undo(glyphName, layerId, historyTargetKey, surface) {
        const targetItem = this._resolveUndoHistoryItem(glyphName, layerId, 'undo', historyTargetKey, surface);
        // Explicit undo surfaces must not fall through to the font stack when
        // the surface filter finds nothing (History bright ≡ Cmd+Z).
        if (surface && !targetItem) {
            return null;
        }
        const target = this._resolveUndoTarget(glyphName, layerId, 'undo', targetItem);
        const { manager: um, scope } = this._getUndoManagerForTarget(target);
        const shouldReplayHistoryItem = this._shouldReplayHistoryItemDirectly(targetItem, 'undo');
        const authoritativeHistory = this._resolveAuthoritativeUndoHistory('undo', targetItem, scope, target.glyphName, target.layerId);
        if (scope !== 'font' &&
            !shouldReplayHistoryItem &&
            (!um ||
                um.undoStack.length === 0 ||
                !authoritativeHistory.historyItem)) {
            if (!authoritativeHistory.historyItem) {
                return null;
            }
        }
        if (scope === 'font' && !authoritativeHistory.historyItem) {
            return null;
        }
        const localUpdateLogIndexBeforeUndo = this._lastLocalUpdateLogIndex;
        const localUpdateBaseline = Y.encodeStateVector(this.yDoc);
        this._suppressRecording = true;
        try {
            const targetHistoryItemId = authoritativeHistory.historyItemId;
            const semanticHistoryItem = authoritativeHistory.historyItem;
            const timestamp = Date.now();
            const metadataEntries = getSemanticEntriesForHistoryItem(semanticHistoryItem, 'undo', targetHistoryItemId, timestamp, this.windowId, this._getWindowRoleLabel());
            if (!metadataEntries?.length && !target.glyphName) {
                return null;
            }
            const workerReplayTargets = normalizeWorkerReplayTargets([
                ...(semanticHistoryItem?.workerReplayTargets ?? []),
                ...(target.glyphName && target.layerId
                    ? [{ glyphName: target.glyphName, layerId: target.layerId }]
                    : [])
            ]);
            // Log entry before um.undo() so it's available when the
            // Y.Doc update event fires and WindowSync broadcasts it.
            const entry = createLogEntry({
                timestamp,
                windowId: this.windowId,
                windowRoleLabel: this._getWindowRoleLabel(),
                historyAction: 'undo',
                targetHistoryItemId,
                transactionLabel: 'Undo',
                transactionId: null,
                op: 'set',
                undoScope: scope,
                path: scope === 'layer' && target.glyphName && target.layerId
                    ? `glyphs.${target.glyphName}.layers.${target.layerId}`
                    : scope === 'glyph' && target.glyphName
                        ? `glyphs.${target.glyphName}`
                        : 'font',
                oldValue: undefined,
                newValue: 'undo',
                workerReplayTargets,
                semanticChangeLogEntries: metadataEntries ?? undefined
            });
            this._appendChangeLogEntry(entry);
            const isHistoryReplay = !!targetItem && (scope === 'font' || shouldReplayHistoryItem);
            const historyReplayWrittenTargets = isHistoryReplay
                ? this._applyHistoryItem(targetItem, 'undo')
                : [];
            if (!isHistoryReplay) {
                um?.undo();
            }
            const layerScopeHints = historyReplayWrittenTargets.length
                ? historyReplayWrittenTargets
                : !(isHistoryReplay && targetItem?.undoScope !== 'layer')
                    ? workerReplayTargets.length > 0 &&
                        workerReplayTargets.every((replayTarget) => !!replayTarget.glyphName &&
                            !!replayTarget.layerId &&
                            this._canPatchLayerFromYDoc(replayTarget))
                        ? workerReplayTargets
                        : scope === 'layer' &&
                            target.glyphName &&
                            target.layerId &&
                            this._canPatchLayerFromYDoc({
                                glyphName: target.glyphName,
                                layerId: target.layerId
                            })
                            ? {
                                glyphName: target.glyphName,
                                layerId: target.layerId
                            }
                            : null
                    : null;
            if (layerScopeHints) {
                this._syncJsonFromYDoc(layerScopeHints);
            }
            else if (metadataEntries?.length) {
                this._syncRemoteJsonFromYDoc(metadataEntries);
            }
            else if (target.glyphName) {
                this._syncRemoteJsonFromYDoc([entry]);
            }
            else {
                throw new Error('Undo requires semantic change metadata for non-layer updates.');
            }
            this._onAfterSync?.();
            this._onDirty?.();
            if (this._lastLocalUpdateLogIndex ===
                localUpdateLogIndexBeforeUndo &&
                !this._isApplyingRemote &&
                !this._suppressAutomaticLocalUpdateEmission) {
                this._emitCanonicalLocalUpdateSince(localUpdateBaseline);
            }
            this._advanceUndoHistoryItem(scope, target.glyphName, target.layerId, 'undo', targetHistoryItemId);
            return {
                scope,
                glyphName: target.glyphName,
                layerId: target.layerId,
                historyItem: semanticHistoryItem
            };
        }
        finally {
            this._suppressRecording = false;
        }
    }
    /**
     * Redo the last undone change.
     *
     * YJS_ONLY: Both the native UndoManager branch and history-replay branch
     * emit a canonical binary Yjs update into the standard post-commit funnel.
     */
    redo(glyphName, layerId, historyTargetKey, surface) {
        const targetItem = this._resolveUndoHistoryItem(glyphName, layerId, 'redo', historyTargetKey, surface);
        if (surface && !targetItem) {
            return null;
        }
        const target = this._resolveUndoTarget(glyphName, layerId, 'redo', targetItem);
        const { manager: um, scope } = this._getUndoManagerForTarget(target);
        const shouldReplayHistoryItem = this._shouldReplayHistoryItemDirectly(targetItem, 'redo');
        const authoritativeHistory = this._resolveAuthoritativeUndoHistory('redo', targetItem, scope, target.glyphName, target.layerId);
        if (scope !== 'font' &&
            !shouldReplayHistoryItem &&
            (!um ||
                um.redoStack.length === 0 ||
                !authoritativeHistory.historyItem)) {
            if (!authoritativeHistory.historyItem) {
                return null;
            }
        }
        if (scope === 'font' && !authoritativeHistory.historyItem) {
            return null;
        }
        const localUpdateLogIndexBeforeRedo = this._lastLocalUpdateLogIndex;
        const localUpdateBaseline = Y.encodeStateVector(this.yDoc);
        this._suppressRecording = true;
        try {
            const targetHistoryItemId = authoritativeHistory.historyItemId;
            const semanticHistoryItem = authoritativeHistory.historyItem;
            const timestamp = Date.now();
            const metadataEntries = getSemanticEntriesForHistoryItem(semanticHistoryItem, 'redo', targetHistoryItemId, timestamp, this.windowId, this._getWindowRoleLabel());
            if (!metadataEntries?.length && !target.glyphName) {
                return null;
            }
            const workerReplayTargets = normalizeWorkerReplayTargets([
                ...(semanticHistoryItem?.workerReplayTargets ?? []),
                ...(target.glyphName && target.layerId
                    ? [{ glyphName: target.glyphName, layerId: target.layerId }]
                    : [])
            ]);
            // Log entry before um.redo() so it's available for broadcast.
            const entry = createLogEntry({
                timestamp,
                windowId: this.windowId,
                windowRoleLabel: this._getWindowRoleLabel(),
                historyAction: 'redo',
                targetHistoryItemId,
                transactionLabel: 'Redo',
                transactionId: null,
                op: 'set',
                undoScope: scope,
                path: scope === 'layer' && target.glyphName && target.layerId
                    ? `glyphs.${target.glyphName}.layers.${target.layerId}`
                    : scope === 'glyph' && target.glyphName
                        ? `glyphs.${target.glyphName}`
                        : 'font',
                oldValue: undefined,
                newValue: 'redo',
                workerReplayTargets,
                semanticChangeLogEntries: metadataEntries ?? undefined
            });
            this._appendChangeLogEntry(entry);
            const isHistoryReplay = !!targetItem && (scope === 'font' || shouldReplayHistoryItem);
            const historyReplayWrittenTargets = isHistoryReplay
                ? this._applyHistoryItem(targetItem, 'redo')
                : [];
            if (!isHistoryReplay) {
                um?.redo();
            }
            const layerScopeHints = historyReplayWrittenTargets.length
                ? historyReplayWrittenTargets
                : !(isHistoryReplay && targetItem?.undoScope !== 'layer')
                    ? workerReplayTargets.length > 0 &&
                        workerReplayTargets.every((replayTarget) => !!replayTarget.glyphName &&
                            !!replayTarget.layerId &&
                            this._canPatchLayerFromYDoc(replayTarget))
                        ? workerReplayTargets
                        : scope === 'layer' &&
                            target.glyphName &&
                            target.layerId &&
                            this._canPatchLayerFromYDoc({
                                glyphName: target.glyphName,
                                layerId: target.layerId
                            })
                            ? {
                                glyphName: target.glyphName,
                                layerId: target.layerId
                            }
                            : null
                    : null;
            if (layerScopeHints) {
                this._syncJsonFromYDoc(layerScopeHints);
            }
            else if (metadataEntries?.length) {
                this._syncRemoteJsonFromYDoc(metadataEntries);
            }
            else if (target.glyphName) {
                this._syncRemoteJsonFromYDoc([entry]);
            }
            else {
                throw new Error('Redo requires semantic change metadata for non-layer updates.');
            }
            this._onAfterSync?.();
            this._onDirty?.();
            if (this._lastLocalUpdateLogIndex ===
                localUpdateLogIndexBeforeRedo &&
                !this._isApplyingRemote &&
                !this._suppressAutomaticLocalUpdateEmission) {
                this._emitCanonicalLocalUpdateSince(localUpdateBaseline);
            }
            this._advanceUndoHistoryItem(scope, target.glyphName, target.layerId, 'redo', targetHistoryItemId);
            return {
                scope,
                glyphName: target.glyphName,
                layerId: target.layerId,
                historyItem: semanticHistoryItem
            };
        }
        finally {
            this._suppressRecording = false;
        }
    }
    /** Check if undo is available. */
    canUndo(glyphName, layerId, historyTargetKey, surface) {
        const targetItem = this._resolveUndoHistoryItem(glyphName, layerId, 'undo', historyTargetKey, surface);
        if (surface && !targetItem) {
            return false;
        }
        const target = this._resolveUndoTarget(glyphName, layerId, 'undo', targetItem);
        const { manager: um, scope } = this._getUndoManagerForTarget(target);
        const authoritativeHistory = this._resolveAuthoritativeUndoHistory('undo', targetItem, scope, target.glyphName, target.layerId);
        if (scope === 'font') {
            return !!authoritativeHistory.historyItem;
        }
        if (this._shouldReplayHistoryItemDirectly(targetItem, 'undo')) {
            return true;
        }
        return (!!authoritativeHistory.historyItem &&
            !!um &&
            um.undoStack.length > 0);
    }
    /** Check if redo is available. */
    canRedo(glyphName, layerId, historyTargetKey, surface) {
        const targetItem = this._resolveUndoHistoryItem(glyphName, layerId, 'redo', historyTargetKey, surface);
        if (surface && !targetItem) {
            return false;
        }
        const target = this._resolveUndoTarget(glyphName, layerId, 'redo', targetItem);
        const { manager: um, scope } = this._getUndoManagerForTarget(target);
        const authoritativeHistory = this._resolveAuthoritativeUndoHistory('redo', targetItem, scope, target.glyphName, target.layerId);
        if (scope === 'font') {
            return !!authoritativeHistory.historyItem;
        }
        if (targetItem &&
            this._shouldReplayHistoryItemDirectly(targetItem, 'redo')) {
            return true;
        }
        return (!!authoritativeHistory.historyItem &&
            !!um &&
            um.redoStack.length > 0);
    }
    // ── Cross-window ─────────────────────────────────────────────
    /**
     * Apply a remote Y.Doc update from another window.
     * Optionally import accompanying change log entries.
     *
     * YJS_ONLY (binary): The remote binary Yjs update is applied and
     * the worker also receives it via _yjsWorkerCallback.
     * Remote JSON/model sync must stay branch-scoped: top-level keys, glyphs,
     * and layers are patched from the authoritative Y.Doc without any full
     * yDocToJson rebuild on the receiving window hot path.
     */
    applyRemoteUpdate(update, remoteEntries, remoteCollaborationMessages) {
        this._isApplyingRemote = true;
        try {
            if (!this._fontJson)
                this._fontJson = {};
            const effectiveRemoteEntries = remoteEntries?.length
                ? remoteEntries
                : (remoteCollaborationMessages?.flatMap((message) => createChangeLogEntriesFromCollaborationMessageEnvelope(message, {
                    windowRoleLabel: this._getWindowRoleLabel()
                })) ?? []);
            if (!effectiveRemoteEntries.length) {
                if (this._isRemoteUpdateNoop(update)) {
                    return false;
                }
                throw new MetadataFreeRemoteUpdateError();
            }
            // Apply linked-window updates using the shared same-user origin so
            // every window can undo the combined edit history.
            let didChange = false;
            const observeRemoteTransaction = (transaction) => {
                didChange ||=
                    transaction.changed.size > 0 ||
                        transaction.deleteSet.clients.size > 0 ||
                        [...transaction.afterState].some(([client, clock]) => clock > (transaction.beforeState.get(client) ?? 0));
            };
            this.yDoc.on('afterTransaction', observeRemoteTransaction);
            try {
                Y.applyUpdate(this.yDoc, update, this._getRemoteUpdateOrigin(effectiveRemoteEntries));
            }
            finally {
                this.yDoc.off('afterTransaction', observeRemoteTransaction);
            }
            if (!didChange) {
                return false;
            }
            if (effectiveRemoteEntries?.length) {
                const glyphNames = new Set(effectiveRemoteEntries
                    .map((entry) => this._deriveGlyphNameFromPath(entry.path))
                    .filter((glyphName) => !!glyphName));
                for (const glyphName of glyphNames) {
                    this.getGlyphUndoManager(glyphName);
                }
                for (const entry of effectiveRemoteEntries) {
                    const glyphName = this._deriveGlyphNameFromPath(entry.path);
                    const layerId = this._deriveLayerIdFromPath(entry.path);
                    if (glyphName && layerId) {
                        this.getLayerUndoManager(glyphName, layerId);
                    }
                }
            }
            this._syncRemoteJsonFromYDoc(effectiveRemoteEntries);
            this._yjsWorkerCallback?.(update, effectiveRemoteEntries);
            this._onAfterSync?.();
            this._onDirty?.();
            if (effectiveRemoteEntries && effectiveRemoteEntries.length > 0) {
                this._appendChangeLogEntries(effectiveRemoteEntries);
                this._lastBroadcastLogIndex = this._changeLog.length;
                this._lastLocalUpdateLogIndex = this._changeLog.length;
            }
            if (remoteCollaborationMessages?.length) {
                this._appendCollaborationLogItems(remoteCollaborationMessages.map((message) => this._createCollaborationLogItem(message, update, 'remote', this._deriveForwardChangesFromCollaborationMessage(message))));
            }
            this._onRemoteChange?.(effectiveRemoteEntries ?? []);
            for (const cb of this._committedChangeListeners) {
                cb(effectiveRemoteEntries ?? [], {
                    origin: 'remote',
                    update
                });
            }
            this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
            return didChange;
        }
        finally {
            this._isApplyingRemote = false;
        }
    }
    _isRemoteUpdateNoop(update) {
        const decodedUpdate = Y.decodeUpdate(update);
        for (const struct of decodedUpdate.structs) {
            const knownClock = Y.getState(this.yDoc.store, struct.id.client);
            if (struct.id.clock + struct.length > knownClock) {
                return false;
            }
        }
        for (const [client, deleteItems] of decodedUpdate.ds.clients) {
            const structs = this.yDoc.store.clients.get(client);
            if (!structs) {
                return false;
            }
            for (const deleteItem of deleteItems) {
                const deleteEnd = deleteItem.clock + deleteItem.len;
                if (Y.getState(this.yDoc.store, client) < deleteEnd) {
                    return false;
                }
                let index = Y.findIndexSS(structs, deleteItem.clock);
                while (index < structs.length &&
                    structs[index].id.clock < deleteEnd) {
                    const struct = structs[index];
                    if (struct instanceof Y.Item && !struct.deleted) {
                        return false;
                    }
                    index += 1;
                }
            }
        }
        return true;
    }
    /**
     * Patch live babelfont JSON for a remote Yjs update using only the
     * touched branches identified by change-log metadata.
     */
    _syncRemoteJsonFromYDoc(remoteEntries) {
        if (!this._fontJson) {
            return;
        }
        const entries = remoteEntries ?? [];
        const layerTargets = new Map();
        const glyphNames = new Set();
        const topLevelKeys = new Set();
        let syncEntireFont = entries.length === 0;
        for (const entry of entries) {
            const pathSegments = this._getPathSegments(String(entry.path || ''));
            if (!pathSegments.length) {
                syncEntireFont = true;
                continue;
            }
            const topLevelKey = pathSegments[0];
            if (topLevelKey === 'font') {
                syncEntireFont = true;
                continue;
            }
            if (topLevelKey !== 'glyphs') {
                topLevelKeys.add(topLevelKey);
                continue;
            }
            const glyphName = this._deriveGlyphNameFromPath(entry.path);
            const layerId = this._deriveLayerIdFromPath(entry.path);
            const explicitLayerTargets = normalizeWorkerReplayTargets(entry.workerReplayTargets);
            const hasExplicitLayerTargets = explicitLayerTargets.length > 0;
            if (!glyphName) {
                syncEntireFont = true;
                continue;
            }
            const isWholeGlyphChange = !layerId && entry.undoScope !== 'layer';
            const isWholeLayerChange = layerId !== null &&
                pathSegments.length === 4 &&
                pathSegments[2] === 'layers' &&
                !hasExplicitLayerTargets;
            if (isWholeGlyphChange || isWholeLayerChange) {
                glyphNames.add(glyphName);
                continue;
            }
        }
        if (!syncEntireFont) {
            for (const entry of entries) {
                const explicitLayerTargets = normalizeWorkerReplayTargets(entry.workerReplayTargets);
                if (explicitLayerTargets.length > 0) {
                    for (const target of explicitLayerTargets) {
                        if (glyphNames.has(target.glyphName)) {
                            continue;
                        }
                        layerTargets.set(getLayerFingerprintTargetKey(target.glyphName, target.layerId), target);
                    }
                    continue;
                }
                const glyphName = this._deriveGlyphNameFromPath(entry.path);
                const layerId = this._deriveLayerIdFromPath(entry.path);
                if (!glyphName || !layerId || glyphNames.has(glyphName)) {
                    continue;
                }
                layerTargets.set(getLayerFingerprintTargetKey(glyphName, layerId), { glyphName, layerId });
            }
        }
        const fingerprintTargets = this._collectRemoteFingerprintTargets(syncEntireFont, glyphNames, Array.from(layerTargets.values()));
        const previousFingerprintSnapshot = fingerprintTargets !== undefined
            ? this._collectLayerFingerprintSnapshot(fingerprintTargets)
            : null;
        if (syncEntireFont) {
            throw new Error('Remote Yjs updates require scoped change metadata.');
        }
        else {
            for (const glyphName of glyphNames) {
                this._patchGlyphFromYDoc(glyphName);
            }
            const fallbackGlyphNames = new Set();
            for (const target of layerTargets.values()) {
                if (glyphNames.has(target.glyphName)) {
                    continue;
                }
                if (!this._patchLayerFromYDoc(target)) {
                    fallbackGlyphNames.add(target.glyphName);
                }
            }
            for (const glyphName of fallbackGlyphNames) {
                this._patchGlyphFromYDoc(glyphName);
            }
            for (const key of topLevelKeys) {
                this._syncTopLevelFontKeyFromYDoc(key);
            }
        }
        this._applyExplicitPropertyRemovalsToFontJson(entries);
        if (previousFingerprintSnapshot) {
            this._emitLayerFingerprintChangedEvents(previousFingerprintSnapshot, this._collectLayerFingerprintSnapshot(fingerprintTargets));
        }
    }
    /**
     * Rehydrate every top-level font key and glyph snapshot from the current
     * Y.Doc after an explicit full-state bootstrap or rebaseline.
     */
    _rehydrateEntireFontJsonFromYDoc() {
        if (!this._fontJson) {
            return;
        }
        const fontRecord = this._fontJson;
        const nextTopLevelKeys = new Set();
        this.fontMap.forEach((_value, key) => {
            nextTopLevelKeys.add(key);
        });
        this._syncAllGlyphsFromYDoc();
        for (const key of nextTopLevelKeys) {
            if (key === 'glyphs' || key === 'glyphOrder') {
                continue;
            }
            this._syncTopLevelFontKeyFromYDoc(key);
        }
        for (const key of Object.keys(fontRecord)) {
            if (key === 'glyphs' || key === 'glyphOrder') {
                continue;
            }
            if (!nextTopLevelKeys.has(key)) {
                delete fontRecord[key];
            }
        }
    }
    _collectRemoteFingerprintTargets(syncEntireFont, glyphNames, layerTargets) {
        if (syncEntireFont) {
            return null;
        }
        const targets = normalizeWorkerReplayTargets(layerTargets);
        for (const glyphName of glyphNames) {
            this._appendGlyphLayerFingerprintTargets(glyphName, targets);
        }
        return targets.length > 0
            ? normalizeWorkerReplayTargets(targets)
            : undefined;
    }
    _appendGlyphLayerFingerprintTargets(glyphName, targets) {
        const seen = new Set(targets.map((target) => getLayerFingerprintTargetKey(target.glyphName, target.layerId)));
        const pushTarget = (layerId) => {
            if (!layerId) {
                return;
            }
            const targetKey = getLayerFingerprintTargetKey(glyphName, layerId);
            if (seen.has(targetKey)) {
                return;
            }
            seen.add(targetKey);
            targets.push({ glyphName, layerId });
        };
        const glyphs = Array.isArray(this._fontJson?.glyphs)
            ? this._fontJson.glyphs.find((glyph) => glyph?.name === glyphName)
            : undefined;
        const existingLayers = Array.isArray(glyphs?.layers)
            ? glyphs.layers
            : [];
        for (const layer of existingLayers) {
            pushTarget(typeof layer?.id === 'string' ? layer.id : null);
        }
        const yGlyphsMap = this.fontMap.get('glyphs');
        if (!(yGlyphsMap instanceof Y.Map)) {
            return;
        }
        const glyphMap = yGlyphsMap.get(glyphName);
        if (!(glyphMap instanceof Y.Map)) {
            return;
        }
        const layersMap = glyphMap.get('layers');
        if (!(layersMap instanceof Y.Map)) {
            return;
        }
        layersMap.forEach((_value, layerId) => {
            pushTarget(layerId);
        });
    }
    _syncAllGlyphsFromYDoc() {
        if (!this._fontJson) {
            return;
        }
        const fontRecord = this._fontJson;
        const yGlyphsMap = this.fontMap.get('glyphs');
        if (!(yGlyphsMap instanceof Y.Map)) {
            delete fontRecord.glyphs;
            return;
        }
        const existingGlyphs = Array.isArray(fontRecord.glyphs)
            ? fontRecord.glyphs
            : [];
        const existingGlyphsByName = new Map(existingGlyphs
            .filter((glyph) => !!glyph && typeof glyph?.name === 'string')
            .map((glyph) => [String(glyph.name), glyph]));
        const nextGlyphs = [];
        const glyphOrder = this.fontMap.get('glyphOrder');
        const orderedGlyphNames = glyphOrder instanceof Y.Array
            ? glyphOrder.toArray().map(String)
            : Array.from(yGlyphsMap.keys());
        const includedGlyphNames = new Set(orderedGlyphNames);
        yGlyphsMap.forEach((_value, glyphName) => {
            if (!includedGlyphNames.has(glyphName)) {
                orderedGlyphNames.push(glyphName);
            }
        });
        for (const glyphName of orderedGlyphNames) {
            const glyphSnapshot = this._readNormalizedGlyphSnapshotFromYDoc(glyphName, existingGlyphsByName.get(glyphName));
            if (glyphSnapshot) {
                nextGlyphs.push(glyphSnapshot);
            }
        }
        fontRecord.glyphs = nextGlyphs;
    }
    _syncTopLevelFontKeyFromYDoc(key) {
        if (!this._fontJson) {
            return;
        }
        if (key === 'glyphs' || key === 'glyphOrder') {
            this._syncAllGlyphsFromYDoc();
            return;
        }
        const fontRecord = this._fontJson;
        const value = this.fontMap.get(key);
        if (value === undefined) {
            delete fontRecord[key];
            return;
        }
        fontRecord[key] = this._cloneRuntimeValue(cloneHistoryValue(fromYType(value)));
    }
    _readNormalizedGlyphSnapshotFromYDoc(glyphName, existingGlyphSnapshot) {
        const glyphsMap = this.fontMap.get('glyphs');
        if (!(glyphsMap instanceof Y.Map)) {
            return null;
        }
        const glyphMap = glyphsMap.get(glyphName);
        if (!(glyphMap instanceof Y.Map)) {
            return null;
        }
        const glyphSnapshot = this._cloneRuntimeValue(this._normalizeGlyphSnapshot(fromYType(glyphMap), existingGlyphSnapshot));
        return glyphSnapshot;
    }
    _patchGlyphFromYDoc(glyphName) {
        if (!this._fontJson) {
            return false;
        }
        const fontRecord = this._fontJson;
        const glyphs = Array.isArray(fontRecord.glyphs)
            ? fontRecord.glyphs
            : [];
        if (!Array.isArray(fontRecord.glyphs)) {
            fontRecord.glyphs = glyphs;
        }
        const glyphIndex = glyphs.findIndex((glyph) => glyph?.name === glyphName);
        const glyphSnapshot = this._readNormalizedGlyphSnapshotFromYDoc(glyphName, glyphIndex >= 0 ? glyphs[glyphIndex] : undefined);
        if (!glyphSnapshot) {
            if (glyphIndex >= 0) {
                glyphs.splice(glyphIndex, 1);
            }
            return true;
        }
        if (glyphIndex >= 0) {
            glyphs[glyphIndex] = glyphSnapshot;
            return true;
        }
        const yGlyphsMap = this.fontMap.get('glyphs');
        if (!(yGlyphsMap instanceof Y.Map)) {
            glyphs.push(glyphSnapshot);
            return true;
        }
        // Prefer glyphOrder over Y.Map insertion order so newly added glyphs
        // land next to their namesake instead of at the end of the array.
        const glyphOrder = this.fontMap.get('glyphOrder');
        const orderedGlyphNames = glyphOrder instanceof Y.Array
            ? glyphOrder.toArray().map(String)
            : [];
        if (orderedGlyphNames.length === 0) {
            yGlyphsMap.forEach((_value, nextGlyphName) => {
                orderedGlyphNames.push(nextGlyphName);
            });
        }
        const glyphOrderIndex = orderedGlyphNames.indexOf(glyphName);
        if (glyphOrderIndex < 0) {
            glyphs.push(glyphSnapshot);
            return true;
        }
        const followingGlyphName = orderedGlyphNames
            .slice(glyphOrderIndex + 1)
            .find((candidateGlyphName) => glyphs.some((glyph) => glyph?.name === candidateGlyphName));
        if (!followingGlyphName) {
            glyphs.push(glyphSnapshot);
            return true;
        }
        const insertIndex = glyphs.findIndex((glyph) => glyph?.name === followingGlyphName);
        if (insertIndex < 0) {
            glyphs.push(glyphSnapshot);
            return true;
        }
        glyphs.splice(insertIndex, 0, glyphSnapshot);
        return true;
    }
    _applyExplicitPropertyRemovalsToFontJson(remoteEntries) {
        if (!remoteEntries?.length || !this._fontJson) {
            return;
        }
        const glyphs = Array.isArray(this._fontJson.glyphs)
            ? this._fontJson.glyphs
            : null;
        if (!glyphs) {
            return;
        }
        for (const entry of remoteEntries) {
            if (entry.op !== 'remove' || entry.historyAction === 'undo') {
                continue;
            }
            const pathSegments = this._getPathSegments(String(entry.path || ''));
            if (pathSegments[0] !== 'glyphs') {
                continue;
            }
            const glyphName = pathSegments[1];
            const glyphRecord = glyphs.find((glyph) => glyph?.name === glyphName);
            if (!glyphRecord) {
                continue;
            }
            if (pathSegments.length === 3) {
                delete glyphRecord[pathSegments[2]];
                continue;
            }
            if (pathSegments.length === 4 && pathSegments[2] === 'layers') {
                const layerId = pathSegments[3];
                const layers = Array.isArray(glyphRecord.layers)
                    ? glyphRecord.layers
                    : null;
                if (!layers) {
                    continue;
                }
                const layerIndex = layers.findIndex((layer) => layer?.id === layerId);
                if (layerIndex >= 0) {
                    layers.splice(layerIndex, 1);
                }
                continue;
            }
            if (pathSegments.length !== 5 || pathSegments[2] !== 'layers') {
                continue;
            }
            const layerId = pathSegments[3];
            const propertyKey = pathSegments[4];
            const layers = Array.isArray(glyphRecord.layers)
                ? glyphRecord.layers
                : null;
            const layerRecord = layers?.find((layer) => layer?.id === layerId);
            if (!layerRecord) {
                continue;
            }
            delete layerRecord[propertyKey];
        }
    }
    /**
     * Export the full Y.Doc state for bootstrapping a new window.
     */
    getFullState() {
        // YJS_ONLY (N2/B3): Binary Yjs state, not JSON.
        // Used for linked-window bootstrap and cloud-font baseline.
        return Y.encodeStateAsUpdate(this.yDoc);
    }
    /**
     * Apply a full state snapshot (for new window bootstrap).
     * The receiving window should NOT call initFromJson() before this —
     * independently initialised Y.Docs have conflicting CRDT state.
     *
     * YJS_ONLY (N2): Binary Yjs state — no JSON crossing.
     * The explicit rehydrate below is reserved for bootstrap and rebaseline,
     * never for an ordinary committed update.
     */
    applyFullState(state) {
        this._isApplyingRemote = true;
        try {
            if (!this._fontJson)
                this._fontJson = {};
            Y.applyUpdate(this.yDoc, state, SYSTEM_REMOTE_ORIGIN);
            this._rehydrateEntireFontJsonFromYDoc();
            this._canonicalizeFullStateRawFontJson();
            // Set up undo managers so this window can undo/redo too
            this._setupFontUndoManager();
            this._onAfterSync?.();
            this._onRemoteChange?.([]);
            this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
        }
        finally {
            this._isApplyingRemote = false;
        }
    }
    /** Encode the full Y.Doc state as a Yjs update binary. */
    encodeBridgeState() {
        return Y.encodeStateAsUpdate(this.yDoc);
    }
    /** Encode the Y.Doc state vector (compact — one entry per known client). */
    encodeBridgeStateVector() {
        return Y.encodeStateVector(this.yDoc);
    }
    /**
     * Encode the minimal update diff that a peer (described by peerStateVector)
     * is missing. Returns an empty update if we have nothing new to share.
     */
    encodeStateDiff(peerStateVector) {
        return Y.encodeStateAsUpdate(this.yDoc, peerStateVector);
    }
    /**
     * Register a callback that will be called with every binary Yjs update
     * (both local edits and remote/undo changes) and the accompanying
     * ChangeLogEntry list. Pass `null` to unregister.
     *
     * The callback is intended to forward updates to the WASM compilation
     * worker so it can maintain its own Rust Y.Doc without receiving the full
     * font JSON on every edit.
     */
    setYjsWorkerCallback(cb) {
        this._yjsWorkerCallback = cb;
    }
    /**
     * Apply a Yjs update directly to the Y.Doc without triggering compilation
     * or JSON synchronisation.  Used by CloudAdapter to re-seed the Y.Doc
     * after a bridge replacement (fontModelReady) so that subsequent
     * incremental updates from remote peers can be applied (their left-sibling
     * references will be resolvable).
     */
    applyYDocUpdateSilent(update) {
        if (!update || update.length === 0)
            return;
        Y.applyUpdate(this.yDoc, update);
        this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
    }
    applyLocalGeneratedYjsUpdate(update, operations, label, historyTarget) {
        const normalizedOperations = operations
            .filter((operation) => operation.path.length > 0)
            .map((operation) => this._normalizeBufferedOperation(operation));
        if (!update?.length || !normalizedOperations.length) {
            return null;
        }
        if (this._txDepth > 0) {
            throw new Error('applyLocalGeneratedYjsUpdate does not support open transactions');
        }
        const scopeInfo = this._deriveBufferedScope(normalizedOperations);
        this._prepareBatchUndoManagers(scopeInfo);
        const nextHistoryItemId = this._createHistoryItemId();
        const timestamp = Date.now();
        const undoSurfaceAffinity = this._resolveCommitUndoSurfaceAffinity({
            label,
            historyTarget,
            operations: normalizedOperations
        });
        const originatingLayer = this._resolveCommitOriginatingLayer(normalizedOperations);
        const changeLogEntries = normalizedOperations.map((operation) => {
            const operationHistoryTarget = historyTarget ?? this._deriveHistoryTarget(operation.path);
            const workerReplayTargets = this._deriveWorkerReplayTargets(operation);
            return createLogEntry({
                timestamp,
                windowId: this.windowId,
                windowRoleLabel: this._getWindowRoleLabel(),
                historyItemId: nextHistoryItemId,
                historyAction: 'change',
                transactionLabel: label,
                transactionId: null,
                op: operation.op,
                undoScope: scopeInfo.scope,
                undoSurfaceAffinity,
                path: joinPathWithGlyphSeparator(operation.path),
                oldValue: operation.oldValue,
                newValue: operation.newValue,
                compileChangeSource: operation.compileChangeSource ?? null,
                compileEditType: operation.compileEditType ?? null,
                replayOldValue: operation.op !== 'set'
                    ? undefined
                    : cloneHistoryValue(operation.applyOldValue === undefined
                        ? operation.oldValue
                        : operation.applyOldValue),
                replayNewValue: operation.op !== 'set'
                    ? undefined
                    : cloneHistoryValue(operation.applyNewValue === undefined
                        ? operation.newValue
                        : operation.applyNewValue),
                visualAnchorSide: operation.visualAnchorSide ?? null,
                workerReplayTargets,
                glyphRenames: operation.glyphRenames,
                historyTargetType: operationHistoryTarget?.type ?? null,
                historyTargetKey: operationHistoryTarget?.key ?? null,
                historyTargetLabel: operationHistoryTarget?.label ?? null,
                originatingGlyphName: originatingLayer.glyphName,
                originatingLayerId: originatingLayer.layerId
            });
        });
        this._appendChangeLogEntries(changeLogEntries);
        const localUpdateLogIndexBeforeCommit = this._lastLocalUpdateLogIndex;
        this._suppressAutomaticLocalUpdateEmission = true;
        try {
            Y.applyUpdate(this.yDoc, update, scopeInfo.origin);
            this._syncRemoteJsonFromYDoc(changeLogEntries);
            this._onAfterSync?.();
            this._onDirty?.();
            this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
            if (this._lastLocalUpdateLogIndex ===
                localUpdateLogIndexBeforeCommit &&
                !this._isApplyingRemote) {
                this._lastLocalUpdateLogIndex = this._changeLog.length;
                this._emitLocalUpdate(update, changeLogEntries);
            }
        }
        finally {
            this._suppressAutomaticLocalUpdateEmission = false;
            this._finishBatchUndoManagers(scopeInfo);
        }
        return {
            changeLogEntries,
            workerReplayTargets: normalizeWorkerReplayTargets(changeLogEntries.flatMap((entry) => entry.workerReplayTargets)),
            changedGlyphNames: [
                ...new Set(changeLogEntries
                    .map((entry) => deriveGlyphNameFromPath(entry.path))
                    .filter((glyphName) => Boolean(glyphName)))
            ],
            changedLayerIds: [
                ...new Set(changeLogEntries
                    .map((entry) => deriveLayerIdFromPath(entry.path))
                    .filter((layerId) => Boolean(layerId)))
            ]
        };
    }
    // ── Change log ───────────────────────────────────────────────
    /** Get the full change log. */
    getChangeLog() {
        return this._changeLog;
    }
    getCollaborationLog() {
        return this._collaborationLog;
    }
    getChangeLogForGlyph(glyphName) {
        if (!glyphName) {
            return this._changeLog;
        }
        return this._changeLog.filter((entry) => this._deriveGlyphNameFromPath(entry.path) === glyphName);
    }
    /** Import change log entries (e.g. from another window). */
    importChangeLog(entries) {
        this._changeLog = entries.map((entry) => normalizeChangeLogEntry(entry));
        this._lastBroadcastLogIndex = this._changeLog.length;
        this._lastLocalUpdateLogIndex = this._changeLog.length;
        this._notifyChangeLogListeners();
    }
    importCollaborationMessages(messages) {
        this._collaborationLog = [...messages];
        this._notifyCollaborationLogListeners();
    }
    /**
     * Replace the imported baseline while preserving unsent local entries.
     * Used by cloud bootstrap so reconnects do not discard offline edits.
     */
    mergeImportedChangeLog(entries) {
        const importedEntries = entries.map((entry) => normalizeChangeLogEntry(entry));
        const pendingBroadcastEntries = this._changeLog
            .slice(this._lastBroadcastLogIndex)
            .map((entry) => normalizeChangeLogEntry(entry));
        const mergedEntries = [...importedEntries, ...pendingBroadcastEntries];
        const seenEntryKeys = new Set();
        this._changeLog = mergedEntries.filter((entry) => {
            const entryKey = [
                entry.windowId,
                String(entry.transactionId),
                String(entry.timestamp),
                entry.historyAction,
                entry.op,
                entry.path
            ].join(':');
            if (seenEntryKeys.has(entryKey)) {
                return false;
            }
            seenEntryKeys.add(entryKey);
            return true;
        });
        this._lastBroadcastLogIndex = importedEntries.length;
        this._lastLocalUpdateLogIndex = this._changeLog.length;
        this._notifyChangeLogListeners();
    }
    mergeImportedCollaborationMessages(messages) {
        const existing = new Map();
        for (const item of [...messages, ...this._collaborationLog]) {
            existing.set(item.id, item);
        }
        this._collaborationLog = [...existing.values()].sort((left, right) => left.timestamp - right.timestamp);
        this._notifyCollaborationLogListeners();
    }
    /**
     * Get change log entries added since the last call.
     * Used by WindowSync to piggyback entries on yjs-update messages.
     */
    getNewChangeLogEntries() {
        const entries = this._changeLog.slice(this._lastBroadcastLogIndex);
        this._lastBroadcastLogIndex = this._changeLog.length;
        return entries;
    }
    advanceBroadcastLogCursor(entryCount) {
        if (!Number.isFinite(entryCount) || entryCount <= 0) {
            return;
        }
        this._lastBroadcastLogIndex = Math.min(this._changeLog.length, this._lastBroadcastLogIndex + Math.floor(entryCount));
    }
    _getNewChangeLogEntriesForLocalUpdate() {
        const entries = this._changeLog.slice(this._lastLocalUpdateLogIndex);
        this._lastLocalUpdateLogIndex = this._changeLog.length;
        return entries;
    }
    /** Reset state (for tests). */
    reset() {
        this._changeLog = [];
        this._collaborationLog = [];
        this._lastBroadcastLogIndex = 0;
        this._lastLocalUpdateLogIndex = 0;
        this._txDepth = 0;
        this._txLabel = null;
        this._txId = null;
        this._txStartTimeMs = null;
        this._txHistoryItemId = null;
        this._txHistoryTarget = null;
        this._txBufferedOperations = [];
        this._nextTxId = 1;
        this._nextHistoryItemId = 1;
        this._nextCollaborationMessageSequence = 1;
        resetLogCounter();
        for (const entry of this._layerUndoManagers.values()) {
            entry.manager.destroy();
        }
        this._layerUndoManagers.clear();
        for (const um of this._undoManagers.values()) {
            um.destroy();
        }
        this._undoManagers.clear();
        this._fontUndoManager?.destroy();
        this._fontUndoManager = null;
        this._undoHistoryStacks.clear();
        this._notifyChangeLogListeners();
        this._notifyCollaborationLogListeners();
    }
    // ── Internal ─────────────────────────────────────────────────
    /**
     * Convert a babelfont JSON path to a Y.Doc path.
     *
     * Glyphs and layers in Y.Doc are keyed by name/ID (Y.Map) rather than
     * array index. So ["glyphs", 0, "layers", 2, "width"] becomes
     * ["glyphs", "A", "layers", "layer-uuid", "width"] etc.
     *
     * Since the model already passes human-readable keys (glyph names,
     * layer IDs) for these segments, this function only needs to ensure
     * numeric array indices remain numbers for Y.Array segments.
     */
    _toYDocPath(path) {
        if (path[0] === 'features' &&
            path[1] === 'features' &&
            typeof path[2] === 'string') {
            const match = String(path[2]).match(/^feature-index:(\d+)$/);
            if (match) {
                return [
                    path[0],
                    path[1],
                    Number.parseInt(match[1], 10),
                    ...path.slice(3)
                ];
            }
        }
        return path;
    }
    /**
     * Sync the local babelfont JSON from the current Y.Doc state.
     * Called after remote updates or undo/redo.
     *
     * YJS_ONLY when fast path succeeds: _patchLayerFromYDoc applies
     * only the touched layers — no full JSON rebuild.
     * When scope hints are unavailable, the live JSON/model is rebuilt from
     * branch-scoped reads of top-level keys and glyph snapshots, not from a
     * monolithic yDocToJson conversion.
     */
    /**
     * Patch the live babelfontData object from the current Y.Doc state.
     *
     * When `scopeHint` is provided (layer-scoped undo/redo), only that one
     * layer is reconstructed from Y.Doc — ~100-1000× faster than a full
     * font rebuild for large fonts. Falls through to the full sync if any
     * Y.Doc path lookup fails.
     *
     * YJS_ONLY when _patchLayerFromYDoc succeeds for all scopeHints
     * (per-layer Y.Doc→JSON, no full rebuild).
     */
    _syncJsonFromYDoc(scopeHints) {
        const normalizedScopeHints = Array.isArray(scopeHints)
            ? normalizeWorkerReplayTargets(scopeHints)
            : scopeHints
                ? normalizeWorkerReplayTargets([scopeHints])
                : [];
        if (!this._fontJson)
            return;
        const fingerprintTargets = normalizedScopeHints.length > 0 ? normalizedScopeHints : null;
        const previousFingerprintSnapshot = this._collectLayerFingerprintSnapshot(fingerprintTargets);
        // Fast path: only reconstruct the touched layers from Y.Doc.
        if (normalizedScopeHints.length > 0 &&
            normalizedScopeHints.every((scopeHint) => this._patchLayerFromYDoc(scopeHint))) {
            this._syncPatchedLayersIntoObjectModel(normalizedScopeHints);
            this._emitLayerFingerprintChangedEvents(previousFingerprintSnapshot, this._collectLayerFingerprintSnapshot(fingerprintTargets));
            return;
        }
        throw new Error('Steady-state Yjs synchronization requires valid layer scope hints.');
    }
    _canPatchLayerFromYDoc(scopeHint) {
        const glyphsMap = this.fontMap.get('glyphs');
        const glyphMap = glyphsMap instanceof Y.Map
            ? glyphsMap.get(scopeHint.glyphName)
            : null;
        const layersMap = glyphMap instanceof Y.Map ? glyphMap.get('layers') : null;
        const glyph = this._fontJson?.glyphs?.find((candidate) => candidate?.name === scopeHint.glyphName);
        return (layersMap instanceof Y.Map &&
            layersMap.get(scopeHint.layerId) instanceof Y.Map &&
            glyph?.layers?.some((layer) => layer?.id === scopeHint.layerId));
    }
    _patchLayerFromYDoc(scopeHint) {
        const glyphsMap = this.fontMap.get('glyphs');
        if (!(glyphsMap instanceof Y.Map)) {
            return false;
        }
        const glyphMap = glyphsMap.get(scopeHint.glyphName);
        if (!(glyphMap instanceof Y.Map)) {
            return false;
        }
        const layersMap = glyphMap.get('layers');
        if (!(layersMap instanceof Y.Map)) {
            return false;
        }
        const layerMap = layersMap.get(scopeHint.layerId);
        if (!(layerMap instanceof Y.Map)) {
            return false;
        }
        const glyphs = this._fontJson.glyphs;
        const glyphIdx = glyphs?.findIndex((g) => g.name === scopeHint.glyphName) ??
            -1;
        if (glyphIdx < 0 || !glyphs) {
            return false;
        }
        const layers = glyphs[glyphIdx].layers;
        const layerIdx = layers?.findIndex((l) => l.id === scopeHint.layerId) ?? -1;
        if (layerIdx < 0 || !layers) {
            return false;
        }
        const patchedLayer = this._cloneRuntimeValue(this._normalizeLayerSnapshot(scopeHint.layerId, fromYType(layerMap), layers[layerIdx]));
        if (patchedLayer &&
            typeof patchedLayer === 'object' &&
            !Array.isArray(patchedLayer)) {
            const layerRecord = patchedLayer;
            if (!('width' in layerRecord)) {
                console.warn(`[PatchSyncEngine] _syncJsonFromYDoc: layer ${scopeHint.layerId} missing "width" after fromYType. Keys: ${Object.keys(layerRecord).join(',')}`);
                const yKeys = [];
                layerMap.forEach((_v, k) => {
                    yKeys.push(k);
                });
                console.warn(`[PatchSyncEngine] Y.Map keys for layer: ${yKeys.join(',')}`);
            }
        }
        layers[layerIdx] = patchedLayer;
        return true;
    }
    _syncPatchedLayersIntoObjectModel(scopeHints) {
        const fontModel = window.fontManager?.currentFont
            ?.fontModel;
        const glyphs = Array.isArray(this._fontJson?.glyphs)
            ? this._fontJson.glyphs
            : [];
        if (!fontModel || glyphs.length === 0) {
            return;
        }
        withSuppressedModelRecording(() => {
            for (const scopeHint of scopeHints) {
                const modelLayer = fontModel
                    .findGlyph?.(scopeHint.glyphName)
                    ?.findLayerById?.(scopeHint.layerId);
                const storedLayer = glyphs
                    .find((glyph) => glyph?.name === scopeHint.glyphName)
                    ?.layers?.find((layer) => layer?.id === scopeHint.layerId);
                if (!modelLayer || !storedLayer) {
                    continue;
                }
                if (typeof modelLayer.syncFromEditorLayerData === 'function') {
                    modelLayer.syncFromEditorLayerData({
                        width: storedLayer.width,
                        ...(storedLayer.height !== undefined
                            ? { height: storedLayer.height }
                            : {}),
                        ...(storedLayer.vertWidth !== undefined
                            ? { vertWidth: storedLayer.vertWidth }
                            : {}),
                        ...(storedLayer.shapes !== undefined
                            ? { shapes: storedLayer.shapes }
                            : {}),
                        ...(storedLayer.anchors !== undefined
                            ? { anchors: storedLayer.anchors }
                            : {}),
                        ...(storedLayer.guides !== undefined
                            ? { guides: storedLayer.guides }
                            : {}),
                        ...(storedLayer.format_specific !== undefined
                            ? { format_specific: storedLayer.format_specific }
                            : {})
                    });
                    modelLayer.invalidateContentCaches?.();
                    continue;
                }
                const rawLayerData = modelLayer.toJSON?.();
                if (rawLayerData && typeof rawLayerData === 'object') {
                    Object.assign(rawLayerData, storedLayer);
                }
                modelLayer.invalidateContentCaches?.();
            }
        });
    }
    _syncPatchedLayerTargetsFromYDoc(scopeHints) {
        const normalizedScopeHints = normalizeWorkerReplayTargets(scopeHints);
        if (!normalizedScopeHints.length || !this._fontJson) {
            return;
        }
        const previousFingerprintSnapshot = this._collectLayerFingerprintSnapshot(normalizedScopeHints);
        const patchedScopeHints = normalizedScopeHints.filter((scopeHint) => this._patchLayerFromYDoc(scopeHint));
        if (!patchedScopeHints.length) {
            return;
        }
        this._syncPatchedLayersIntoObjectModel(patchedScopeHints);
        this._emitLayerFingerprintChangedEvents(previousFingerprintSnapshot, this._collectLayerFingerprintSnapshot(normalizedScopeHints));
    }
    /**
     * Setup the font-level UndoManager (everything outside glyphs).
     * Scoped to the root fontMap but excludes the "glyphs" sub-map.
     */
    _setupFontUndoManager() {
        this._fontUndoManager?.destroy();
        // Track all top-level keys in the font map
        this._fontUndoManager = new Y.UndoManager(this.fontMap, {
            trackedOrigins: new Set([FONT_EDIT_ORIGIN]),
            captureTimeout: 0
        });
    }
    /**
     * Get or create a per-glyph UndoManager.
     */
    getGlyphUndoManager(glyphName) {
        if (this._undoManagers.has(glyphName)) {
            return this._undoManagers.get(glyphName);
        }
        const glyphsMap = this.fontMap.get('glyphs');
        if (!(glyphsMap instanceof Y.Map))
            return null;
        const glyphMap = glyphsMap.get(glyphName);
        if (!(glyphMap instanceof Y.Map))
            return null;
        const um = new Y.UndoManager(glyphMap, {
            trackedOrigins: new Set([GLYPH_EDIT_ORIGIN]),
            captureTimeout: 0
        });
        this._undoManagers.set(glyphName, um);
        return um;
    }
    getLayerUndoManager(glyphName, layerId) {
        const managerKey = getLayerManagerKey(glyphName, layerId);
        const glyphsMap = this.fontMap.get('glyphs');
        if (!(glyphsMap instanceof Y.Map))
            return null;
        const glyphMap = glyphsMap.get(glyphName);
        if (!(glyphMap instanceof Y.Map))
            return null;
        const layersMap = glyphMap.get('layers');
        if (!(layersMap instanceof Y.Map))
            return null;
        const existingEntry = this._layerUndoManagers.get(managerKey);
        if (existingEntry) {
            if (existingEntry.target === layersMap) {
                return existingEntry.manager;
            }
            existingEntry.manager.destroy();
            this._layerUndoManagers.delete(managerKey);
        }
        const um = new Y.UndoManager(layersMap, {
            trackedOrigins: new Set([getLayerEditOrigin(glyphName, layerId)]),
            captureTimeout: 0
        });
        this._layerUndoManagers.set(managerKey, {
            manager: um,
            target: layersMap
        });
        return um;
    }
    _deriveUndoScope(glyphName, layerId) {
        if (!glyphName) {
            return 'font';
        }
        if (layerId) {
            return 'layer';
        }
        return 'glyph';
    }
    _resolveCommitUndoSurfaceAffinity(options) {
        let contextSurface = null;
        try {
            contextSurface = getUndoRedoContext().surface;
        }
        catch {
            contextSurface = null;
        }
        const primary = options.operations[0];
        return resolveUndoSurfaceAffinity({
            contextSurface,
            editSource: primary?.editSource ?? null,
            compileChangeSource: primary?.compileChangeSource ?? null,
            historyTargetKey: options.historyTarget?.key ?? null,
            transactionLabel: options.label ?? null,
            paths: options.operations.map((operation) => joinPathWithGlyphSeparator(operation.path))
        });
    }
    _resolveCommitOriginatingLayer(operations) {
        let contextSurface = null;
        let contextGlyphName = null;
        let contextLayerId = null;
        try {
            const context = getUndoRedoContext();
            contextSurface = context.surface;
            contextGlyphName = context.undoGlyphName ?? null;
            contextLayerId = context.undoLayerId ?? null;
        }
        catch {
            contextSurface = null;
        }
        return resolveCommitOriginatingLayer({
            contextSurface,
            contextGlyphName,
            contextLayerId,
            operations
        });
    }
    _queueOrCommitOperations(operations, label, skipTransactionFinalizer = false) {
        const normalizedOperations = operations
            .filter((operation) => operation.path.length > 0)
            .map((operation) => this._normalizeBufferedOperation(operation));
        if (!normalizedOperations.length) {
            return null;
        }
        if (this._txDepth > 0) {
            // Clone values before buffering — the model may mutate while the
            // transaction is still open.
            this._txBufferedOperations.push(...normalizedOperations.map((op) => this._cloneBufferedOperation(op)));
            return null;
        }
        return this._commitOperations(normalizedOperations, label ?? null, null, null, undefined, undefined, undefined, skipTransactionFinalizer);
    }
    _normalizeBufferedOperation(operation) {
        return {
            op: operation.op,
            path: [...operation.path],
            oldValue: operation.oldValue,
            newValue: operation.newValue,
            editSource: operation.editSource ?? null,
            compileChangeSource: operation.compileChangeSource ?? null,
            compileEditType: operation.compileEditType ?? null,
            visualAnchorSide: operation.visualAnchorSide ?? null,
            workerReplayTargets: normalizeWorkerReplayTargets(operation.workerReplayTargets),
            glyphRenames: normalizeGlyphRenames(operation.glyphRenames),
            applyPath: operation.applyPath
                ? [...operation.applyPath]
                : undefined,
            applyOldValue: operation.applyOldValue,
            applyNewValue: operation.applyNewValue,
            applyMode: operation.applyMode ?? 'default',
            originatingGlyphName: operation.originatingGlyphName ?? null,
            originatingLayerId: operation.originatingLayerId ?? null
        };
    }
    _cloneBufferedOperation(operation) {
        return {
            ...operation,
            path: [...operation.path],
            oldValue: cloneHistoryValue(operation.oldValue),
            newValue: cloneHistoryValue(operation.newValue),
            editSource: operation.editSource ?? null,
            compileChangeSource: operation.compileChangeSource ?? null,
            compileEditType: operation.compileEditType ?? null,
            workerReplayTargets: normalizeWorkerReplayTargets(operation.workerReplayTargets),
            glyphRenames: normalizeGlyphRenames(operation.glyphRenames),
            applyPath: operation.applyPath
                ? [...operation.applyPath]
                : undefined,
            applyOldValue: operation.applyOldValue === undefined
                ? undefined
                : cloneHistoryValue(operation.applyOldValue),
            applyNewValue: operation.applyNewValue === undefined
                ? undefined
                : cloneHistoryValue(operation.applyNewValue),
            applyMode: operation.applyMode ?? 'default'
        };
    }
    _commitOperations(operations, label, transactionId, historyItemId, historyTarget, promptGroupId, historySummary, skipTransactionFinalizer = false) {
        const normalizedOperations = operations.filter((operation) => operation.path.length > 0);
        if (!normalizedOperations.length) {
            return null;
        }
        let finalizedOperations = normalizedOperations;
        if (this._transactionFinalizer && !skipTransactionFinalizer) {
            const derivedOperations = this._transactionFinalizer(normalizedOperations.map((operation) => this._cloneBufferedOperation(operation)), {
                label,
                transactionId,
                historyItemId: historyItemId ?? this._txHistoryItemId,
                historyTarget: historyTarget ?? this._txHistoryTarget
            });
            if (derivedOperations?.length) {
                finalizedOperations = [
                    ...normalizedOperations,
                    ...derivedOperations.map((operation) => this._normalizeBufferedOperation(operation))
                ];
            }
        }
        // Snapshot-mode operations carry a full layer/glyph object and are
        // always material changes, so skip the no-op reduction. Default-mode
        // property changes (e.g. feature code edits) may be no-ops and still
        // need the reduction to filter them out.
        const effectiveOperations = finalizedOperations.length === 1 &&
            (finalizedOperations[0].applyMode === 'font-snapshot' ||
                finalizedOperations[0].applyMode === 'layer-snapshot' ||
                finalizedOperations[0].applyMode === 'glyph-snapshot')
            ? finalizedOperations
            : this._reduceToNetChangingOperations(finalizedOperations);
        if (!effectiveOperations.length) {
            return null;
        }
        const scopeInfo = this._deriveBufferedScope(effectiveOperations);
        this._prepareBatchUndoManagers(scopeInfo);
        const nextHistoryItemId = historyItemId ?? this._getCurrentHistoryItemId();
        const timestamp = Date.now();
        const transactionDurationMs = transactionId !== null && this._txStartTimeMs !== null
            ? Math.max(0, performance.now() - this._txStartTimeMs)
            : null;
        const operationPaths = effectiveOperations.map((operation) => joinPathWithGlyphSeparator(operation.path));
        const primaryHistoryTarget = historyTarget ??
            this._deriveHistoryTarget(effectiveOperations[0]?.path ?? []);
        const undoSurfaceAffinity = this._resolveCommitUndoSurfaceAffinity({
            label,
            historyTarget: primaryHistoryTarget,
            operations: effectiveOperations
        });
        const originatingLayer = this._resolveCommitOriginatingLayer(effectiveOperations);
        const changeLogEntries = [];
        for (const operation of effectiveOperations) {
            const operationHistoryTarget = historyTarget ?? this._deriveHistoryTarget(operation.path);
            const workerReplayTargets = this._deriveWorkerReplayTargets(operation);
            const entry = createLogEntry({
                timestamp,
                windowId: this.windowId,
                windowRoleLabel: this._getWindowRoleLabel(),
                historyItemId: nextHistoryItemId,
                promptGroupId,
                historySummary,
                historyAction: 'change',
                transactionLabel: label,
                transactionId,
                transactionDurationMs,
                op: operation.op,
                undoScope: this._deriveUndoScope(deriveGlyphName(operation.path), deriveLayerId(operation.path)),
                undoSurfaceAffinity,
                path: joinPathWithGlyphSeparator(operation.path),
                oldValue: operation.oldValue,
                newValue: operation.newValue,
                editSource: operation.editSource ?? null,
                compileChangeSource: operation.compileChangeSource ?? null,
                compileEditType: operation.compileEditType ?? null,
                replayOldValue: this._cloneReplayValue(operation.applyOldValue === undefined
                    ? operation.oldValue
                    : operation.applyOldValue),
                replayNewValue: this._cloneReplayValue(operation.applyNewValue === undefined
                    ? operation.newValue
                    : operation.applyNewValue),
                visualAnchorSide: operation.visualAnchorSide ?? null,
                workerReplayTargets,
                glyphRenames: operation.glyphRenames,
                historyTargetType: operationHistoryTarget?.type ?? null,
                historyTargetKey: operationHistoryTarget?.key ?? null,
                historyTargetLabel: operationHistoryTarget?.label ?? null,
                originatingGlyphName: originatingLayer.glyphName,
                originatingLayerId: originatingLayer.layerId
            });
            changeLogEntries.push(entry);
        }
        this._appendChangeLogEntries(changeLogEntries);
        const localUpdateBaseline = this._txStartStateVector ?? this._lastBroadcastStateVector;
        this._suppressAutomaticLocalUpdateEmission = true;
        try {
            this.yDoc.transact(() => {
                for (const operation of effectiveOperations) {
                    this._applyBufferedOperation(operation);
                }
            }, scopeInfo.origin);
        }
        finally {
            this._suppressAutomaticLocalUpdateEmission = false;
        }
        const exactCommitUpdate = Y.encodeStateAsUpdate(this.yDoc, localUpdateBaseline);
        this._lastBroadcastStateVector = Y.encodeStateVector(this.yDoc);
        if (!this._isApplyingRemote) {
            this._lastLocalUpdateLogIndex = this._changeLog.length;
            if (exactCommitUpdate.length > 0) {
                this._emitLocalUpdate(exactCommitUpdate, changeLogEntries);
            }
            else if (changeLogEntries.some((entry) => entry.workerReplayTargets.length > 0)) {
                this._emitLocalUpdate(Y.encodeStateAsUpdate(this.yDoc, Y.encodeStateVector(this.yDoc)), changeLogEntries);
            }
        }
        this._recordUndoHistoryItem(scopeInfo.scope, scopeInfo.glyphName, scopeInfo.layerId, nextHistoryItemId);
        this._finishBatchUndoManagers(scopeInfo);
        this._onDirty?.();
        if (effectiveOperations.length === 1) {
            console.log(`[PatchSyncEngine] Change recorded: ${joinPathWithGlyphSeparator(effectiveOperations[0].path)}`);
        }
        return {
            changeLogEntries,
            workerReplayTargets: normalizeWorkerReplayTargets(changeLogEntries.flatMap((entry) => entry.workerReplayTargets)),
            changedGlyphNames: [
                ...new Set(changeLogEntries
                    .map((entry) => deriveGlyphNameFromPath(entry.path))
                    .filter((glyphName) => Boolean(glyphName)))
            ],
            changedLayerIds: [
                ...new Set(changeLogEntries
                    .map((entry) => deriveLayerIdFromPath(entry.path))
                    .filter((layerId) => Boolean(layerId)))
            ]
        };
    }
    _deriveWorkerReplayTargets(operation) {
        const explicitTargets = normalizeWorkerReplayTargets(operation.workerReplayTargets);
        if (explicitTargets.length) {
            return explicitTargets;
        }
        const applyPath = operation.applyPath ?? operation.path;
        const glyphName = deriveGlyphName(applyPath);
        const layerId = deriveLayerId(applyPath);
        if (glyphName && layerId) {
            return [{ glyphName, layerId }];
        }
        if (operation.applyMode !== 'glyph-snapshot' ||
            operation.op !== 'set' ||
            !glyphName) {
            return [];
        }
        const glyphSnapshot = operation.applyNewValue === undefined
            ? operation.newValue
            : operation.applyNewValue;
        if (!glyphSnapshot || typeof glyphSnapshot !== 'object') {
            return [];
        }
        const layers = Array.isArray(glyphSnapshot.layers)
            ? glyphSnapshot.layers
            : [];
        return normalizeWorkerReplayTargets(layers.map((layer) => {
            const snapshotLayerId = layer && typeof layer === 'object'
                ? String(layer.id || '')
                : '';
            return snapshotLayerId
                ? {
                    glyphName,
                    layerId: snapshotLayerId
                }
                : null;
        }));
    }
    _reduceToNetChangingOperations(operations) {
        const byApplyPath = new Map();
        operations.forEach((operation) => {
            const applyPath = this._toYDocPath(operation.applyPath ?? operation.path);
            const pathKey = JSON.stringify(applyPath);
            const existing = byApplyPath.get(pathKey);
            const finalValue = operation.op === 'remove'
                ? undefined
                : cloneHistoryValue(operation.applyNewValue === undefined
                    ? operation.newValue
                    : operation.applyNewValue);
            if (!existing) {
                byApplyPath.set(pathKey, {
                    finalValue,
                    originalValue: cloneHistoryValue(getYPath(this.fontMap, applyPath))
                });
                return;
            }
            existing.finalValue = finalValue;
        });
        const noOpPathKeys = new Set(Array.from(byApplyPath.entries())
            .filter(([, entry]) => this._isDeepEqual(entry.originalValue, entry.finalValue))
            .map(([pathKey]) => pathKey));
        if (!noOpPathKeys.size) {
            return operations;
        }
        return operations.filter((operation) => {
            const applyPath = this._toYDocPath(operation.applyPath ?? operation.path);
            const pathKey = JSON.stringify(applyPath);
            return !noOpPathKeys.has(pathKey);
        });
    }
    _applyBufferedOperation(operation) {
        if (operation.applyMode === 'font-snapshot') {
            this._applyFontSnapshot(operation.applyNewValue === undefined
                ? operation.newValue
                : operation.applyNewValue);
            return;
        }
        const applyPath = this._toYDocPath(operation.applyPath ?? operation.path);
        const applyValue = operation.applyNewValue === undefined
            ? operation.newValue
            : operation.applyNewValue;
        if (operation.op === 'remove') {
            deleteYPath(this.fontMap, applyPath);
            return;
        }
        if (operation.applyMode === 'glyph-snapshot' &&
            this._isGlyphRootPath(applyPath)) {
            this._applyGlyphSnapshot(String(applyPath[1]), applyValue);
            return;
        }
        if (operation.applyMode === 'layer-snapshot' &&
            applyPath.length === 4 &&
            applyPath[0] === 'glyphs' &&
            applyPath[2] === 'layers') {
            this._applyLayerDelta(String(applyPath[1]), String(applyPath[3]), applyValue);
            return;
        }
        setYPath(this.fontMap, applyPath, applyValue);
    }
    _deriveBufferedScope(operations) {
        const touchedGlyphNames = new Set();
        const touchedLayerKeys = new Set();
        let hasFontScopedChange = false;
        let hasGlyphScopedChange = false;
        for (const operation of operations) {
            const glyphName = deriveGlyphName(operation.path);
            const layerId = deriveLayerId(operation.path);
            if (!glyphName) {
                hasFontScopedChange = true;
            }
            if (glyphName) {
                touchedGlyphNames.add(glyphName);
            }
            if (glyphName && layerId) {
                touchedLayerKeys.add(getLayerManagerKey(glyphName, layerId));
            }
            else if (glyphName) {
                hasGlyphScopedChange = true;
            }
        }
        if (!hasFontScopedChange && touchedGlyphNames.size === 1) {
            const glyphName = [...touchedGlyphNames][0];
            if (!hasGlyphScopedChange && touchedLayerKeys.size === 1) {
                const [managerKey] = [...touchedLayerKeys];
                const [, layerId] = managerKey.split('@@');
                return {
                    scope: 'layer',
                    origin: this._getEditOrigin(glyphName, layerId, 'layer'),
                    glyphName,
                    layerId
                };
            }
            return {
                scope: 'glyph',
                origin: this._getEditOrigin(glyphName, null, 'glyph'),
                glyphName,
                layerId: null
            };
        }
        return {
            scope: 'font',
            origin: FONT_EDIT_ORIGIN,
            glyphName: null,
            layerId: null
        };
    }
    _prepareBatchUndoManagers(scopeInfo) {
        if (scopeInfo.scope === 'layer' &&
            scopeInfo.glyphName &&
            scopeInfo.layerId) {
            this.getLayerUndoManager(scopeInfo.glyphName, scopeInfo.layerId)?.stopCapturing();
            return;
        }
        if (scopeInfo.scope === 'glyph' && scopeInfo.glyphName) {
            this.getGlyphUndoManager(scopeInfo.glyphName)?.stopCapturing();
            return;
        }
        this._fontUndoManager?.stopCapturing();
    }
    _finishBatchUndoManagers(scopeInfo) {
        if (scopeInfo.scope === 'layer' &&
            scopeInfo.glyphName &&
            scopeInfo.layerId) {
            this.getLayerUndoManager(scopeInfo.glyphName, scopeInfo.layerId)?.stopCapturing();
            return;
        }
        if (scopeInfo.scope === 'glyph' && scopeInfo.glyphName) {
            this.getGlyphUndoManager(scopeInfo.glyphName)?.stopCapturing();
            return;
        }
        this._fontUndoManager?.stopCapturing();
    }
    _getEditOrigin(glyphName, layerId, scope) {
        if (scope === 'layer' && glyphName && layerId) {
            return getLayerEditOrigin(glyphName, layerId);
        }
        if (scope === 'glyph') {
            return GLYPH_EDIT_ORIGIN;
        }
        return FONT_EDIT_ORIGIN;
    }
    _getUndoHistoryScopeKey(scope, glyphName, layerId) {
        if (scope === 'layer' && glyphName && layerId) {
            return getLayerManagerKey(glyphName, layerId);
        }
        if (scope === 'glyph' && glyphName) {
            return glyphName;
        }
        return FONT_UNDO_HISTORY_KEY;
    }
    _getUndoHistoryStacksForScope(scope, glyphName, layerId) {
        const key = this._getUndoHistoryScopeKey(scope, glyphName, layerId);
        const existing = this._undoHistoryStacks.get(key);
        if (existing) {
            return existing;
        }
        const created = { active: [], undone: [] };
        this._undoHistoryStacks.set(key, created);
        return created;
    }
    _recordUndoHistoryItem(scope, glyphName, layerId, historyItemId) {
        const stacks = this._getUndoHistoryStacksForScope(scope, glyphName, layerId);
        removeHistoryItemFromStack(stacks.active, historyItemId);
        removeHistoryItemFromStack(stacks.undone, historyItemId);
        stacks.active.push(historyItemId);
        stacks.undone.length = 0;
    }
    _peekUndoHistoryItemId(scope, glyphName, layerId, historyAction) {
        const stacks = this._getUndoHistoryStacksForScope(scope, glyphName, layerId);
        const sourceStack = historyAction === 'undo' ? stacks.active : stacks.undone;
        return sourceStack.length ? sourceStack[sourceStack.length - 1] : null;
    }
    _advanceUndoHistoryItem(scope, glyphName, layerId, historyAction, historyItemId) {
        if (!historyItemId) {
            return;
        }
        const stacks = this._getUndoHistoryStacksForScope(scope, glyphName, layerId);
        const sourceStack = historyAction === 'undo' ? stacks.active : stacks.undone;
        const targetStack = historyAction === 'undo' ? stacks.undone : stacks.active;
        removeHistoryItemFromStack(sourceStack, historyItemId);
        removeHistoryItemFromStack(targetStack, historyItemId);
        targetStack.push(historyItemId);
    }
    _findHistoryItemById(historyItemId) {
        if (!historyItemId) {
            return null;
        }
        return (buildHistoryStackItems(this._changeLog, {
            includeUndone: true
        }).find((item) => item.id === historyItemId) ?? null);
    }
    _resolveAuthoritativeUndoHistory(historyAction, targetItem, scope, glyphName, layerId) {
        const historyItemId = targetItem?.id ??
            this._peekUndoHistoryItemId(scope, glyphName, layerId, historyAction);
        return {
            historyItemId,
            historyItem: targetItem ?? this._findHistoryItemById(historyItemId)
        };
    }
    _deriveBulkUndoScope(targets, layerId) {
        const glyphNames = new Set(targets.map((target) => target.glyphName));
        if (glyphNames.size !== 1) {
            return 'font';
        }
        if (layerId) {
            return 'layer';
        }
        return 'glyph';
    }
    _getRemoteUpdateOrigin(remoteEntries) {
        if (!remoteEntries?.length) {
            return USER_EDIT_ORIGIN;
        }
        const targetItem = remoteEntries.find((entry) => entry.historyAction === 'change');
        const glyphNames = new Set(remoteEntries
            .map((entry) => this._deriveGlyphNameFromPath(entry.path))
            .filter((glyphName) => !!glyphName));
        const layerKeys = new Set(remoteEntries
            .filter((entry) => entry.undoScope === 'layer')
            .map((entry) => {
            const glyphName = this._deriveGlyphNameFromPath(entry.path);
            const layerId = this._deriveLayerIdFromPath(entry.path);
            return glyphName && layerId
                ? getLayerManagerKey(glyphName, layerId)
                : null;
        })
            .filter((key) => !!key));
        if (remoteEntries.some((entry) => entry.undoScope === 'font')) {
            return FONT_EDIT_ORIGIN;
        }
        if (layerKeys.size === 1 && glyphNames.size === 1) {
            const entry = targetItem ??
                remoteEntries.find((candidate) => !!this._deriveGlyphNameFromPath(candidate.path) &&
                    !!this._deriveLayerIdFromPath(candidate.path));
            if (entry) {
                const glyphName = this._deriveGlyphNameFromPath(entry.path);
                const layerId = this._deriveLayerIdFromPath(entry.path);
                if (glyphName && layerId) {
                    return getLayerEditOrigin(glyphName, layerId);
                }
            }
        }
        if (glyphNames.size === 1) {
            return GLYPH_EDIT_ORIGIN;
        }
        return FONT_EDIT_ORIGIN;
    }
    _getRemoteLayerSyncScopes(remoteEntries) {
        if (!remoteEntries?.length) {
            return null;
        }
        if (remoteEntries.some((entry) => {
            const glyphName = this._deriveGlyphNameFromPath(entry.path);
            const layerId = this._deriveLayerIdFromPath(entry.path);
            return !!glyphName && !layerId;
        })) {
            return null;
        }
        const targets = normalizeWorkerReplayTargets(remoteEntries.flatMap((entry) => {
            if (entry.workerReplayTargets?.length) {
                return entry.workerReplayTargets;
            }
            if (entry.undoScope !== 'layer') {
                return [];
            }
            const glyphName = this._deriveGlyphNameFromPath(entry.path);
            const layerId = this._deriveLayerIdFromPath(entry.path);
            return glyphName && layerId ? [{ glyphName, layerId }] : [];
        }));
        if (!targets.length) {
            return null;
        }
        return targets;
    }
    _hasMaterializedLayerRoot(glyphName, layerId) {
        const layerValue = getYPath(this.fontMap, [
            'glyphs',
            glyphName,
            'layers',
            layerId
        ]);
        if (!(layerValue instanceof Y.Map)) {
            return false;
        }
        const layerSnapshot = fromYType(layerValue);
        if (!layerSnapshot ||
            typeof layerSnapshot !== 'object' ||
            Array.isArray(layerSnapshot)) {
            return false;
        }
        const layerRecord = layerSnapshot;
        return (typeof layerRecord.id === 'string' &&
            layerRecord.id.length > 0 &&
            layerRecord.id === layerId);
    }
    _resolveUndoHistoryItem(glyphName, layerId, historyAction, historyTargetKey, surface) {
        const resolvedGlyphName = glyphName ?? null;
        const resolvedLayerId = layerId ?? null;
        const resolvedHistoryTargetKey = historyTargetKey ?? null;
        // Prefer the newest visible history item whose backing UndoManager
        // still has stack depth. This prevents stale change-log entries from
        // blocking undo/redo after branch edits clear a manager's stack.
        const candidates = buildHistoryStackItems(this._changeLog, {
            glyphName: resolvedGlyphName,
            layerId: resolvedLayerId,
            includeUndone: true,
            historyTargetKey: resolvedHistoryTargetKey,
            surface: surface ?? null
        }).filter((item) => historyAction === 'undo' ? item.isActive : !item.isActive);
        for (let index = candidates.length - 1; index >= 0; index--) {
            const candidate = candidates[index];
            if (this._shouldReplayHistoryItemDirectly(candidate, historyAction)) {
                return candidate;
            }
            const target = this._targetFromHistoryItem(candidate, resolvedGlyphName, resolvedLayerId);
            const { manager, scope } = this._getUndoManagerForTarget(target);
            if (scope === 'font') {
                return candidate;
            }
            if (!manager) {
                continue;
            }
            const stackDepth = historyAction === 'undo'
                ? manager.undoStack.length
                : manager.redoStack.length;
            if (stackDepth > 0) {
                return candidate;
            }
        }
        return resolveHistoryTargetItem(this._changeLog, {
            glyphName: resolvedGlyphName,
            layerId: resolvedLayerId,
            historyAction,
            historyTargetKey: resolvedHistoryTargetKey,
            surface: surface ?? null
        });
    }
    _resolveUndoTarget(glyphName, layerId, historyAction, targetItem) {
        if (targetItem) {
            return this._targetFromHistoryItem(targetItem, glyphName ?? null, layerId ?? null);
        }
        return {
            glyphName: glyphName ?? null,
            layerId: layerId ?? null
        };
    }
    _deriveHistoryTarget(path) {
        if (!this._fontJson || path[0] !== 'features' || path.length < 3) {
            return null;
        }
        if (path[1] === 'prefixes' && typeof path[2] === 'string') {
            return {
                type: 'prefix',
                key: `prefix:${path[2]}`,
                label: String(path[2])
            };
        }
        if (path[1] === 'classes' && typeof path[2] === 'string') {
            return {
                type: 'class',
                key: `class:${path[2]}`,
                label: String(path[2])
            };
        }
        if (path[1] !== 'features' || typeof path[2] !== 'number') {
            return null;
        }
        const features = (this._fontJson.features?.features ??
            []);
        const featureIndex = path[2];
        const featureEntry = features[featureIndex];
        if (!featureEntry) {
            return null;
        }
        const tag = String(featureEntry[0] ?? '');
        if (!tag) {
            return {
                type: 'feature',
                key: `feature-index:${featureIndex}`,
                label: `#${featureIndex + 1}`
            };
        }
        let occurrence = 0;
        for (let index = 0; index <= featureIndex; index++) {
            if (String(features[index]?.[0] ?? '') === tag) {
                occurrence += 1;
            }
        }
        return {
            type: 'feature',
            key: `feature:${tag}:${occurrence}`,
            label: occurrence > 1 ? `${tag} #${occurrence}` : tag
        };
    }
    _getPathSegments(path) {
        return getPathSegments(path);
    }
    _deriveGlyphNameFromPath(path) {
        return deriveGlyphNameFromPath(path);
    }
    _deriveLayerIdFromPath(path) {
        return deriveLayerIdFromPath(path);
    }
    _parseEntryPath(path) {
        return this._getPathSegments(path).map((segment) => /^\d+$/.test(segment) ? Number.parseInt(segment, 10) : segment);
    }
    _applyHistoryItem(item, direction) {
        const resettleDerivedLayers = shouldResettleDerivedLayersOnHistoryReplay(item, !!this._getResettleFontModel());
        const orderedEntries = direction === 'undo' ? [...item.entries].reverse() : item.entries;
        const replayEntries = resettleDerivedLayers
            ? orderedEntries.filter((entry) => !isDerivedLayerChangePath(entry.path, item.originatingGlyphName))
            : orderedEntries;
        let writtenTargets = [];
        this.yDoc.transact(() => {
            for (const entry of replayEntries) {
                this._applyHistoryReplayEntry(entry, direction);
            }
            if (resettleDerivedLayers) {
                writtenTargets =
                    this._resettleDerivedLayersAfterOriginReplay(item);
            }
        }, HISTORY_REPLAY_ORIGIN);
        return writtenTargets;
    }
    _applyHistoryReplayEntry(entry, direction) {
        const replayValue = this._getHistoryReplayValue(entry, direction);
        if (entry.path === 'font') {
            this._applyFontSnapshot(replayValue);
            return;
        }
        const path = this._toYDocPath(this._parseEntryPath(entry.path));
        if (this._isGlyphRootPath(path) && replayValue) {
            this._applyGlyphSnapshot(String(path[1]), replayValue);
            return;
        }
        if (path.length === 4 &&
            path[0] === 'glyphs' &&
            path[2] === 'layers' &&
            typeof path[1] === 'string' &&
            typeof path[3] === 'string' &&
            replayValue !== undefined) {
            this._applyLayerDelta(path[1], path[3], replayValue);
            return;
        }
        if (direction === 'undo') {
            if (entry.op === 'add') {
                deleteYPath(this.fontMap, path);
                return;
            }
            if (entry.op === 'remove' || entry.op === 'set') {
                if (entry.op === 'set' && replayValue === undefined) {
                    deleteYPath(this.fontMap, path);
                }
                else {
                    setYPath(this.fontMap, path, replayValue);
                }
            }
            return;
        }
        if (entry.op === 'remove') {
            deleteYPath(this.fontMap, path);
            return;
        }
        setYPath(this.fontMap, path, replayValue);
    }
    _getResettleFontModel() {
        const fontModel = window.fontManager?.currentFont?.fontModel;
        if (!fontModel || typeof fontModel.findGlyph !== 'function') {
            return null;
        }
        if (typeof fontModel.rebuildAutomaticCompositesForGlyphs !==
            'function' &&
            typeof fontModel.recomputeMetricsKeys !== 'function') {
            return null;
        }
        return fontModel;
    }
    _collectOriginLayerTargets(item, entries) {
        return normalizeWorkerReplayTargets([
            item.originatingGlyphName && item.originatingLayerId
                ? {
                    glyphName: item.originatingGlyphName,
                    layerId: item.originatingLayerId
                }
                : null,
            ...entries.map((entry) => ({
                glyphName: deriveGlyphNameFromPath(entry.path) || '',
                layerId: deriveLayerIdFromPath(entry.path) || ''
            }))
        ]);
    }
    _getFontJsonLayer(glyphName, layerId) {
        const glyphs = Array.isArray(this._fontJson?.glyphs)
            ? this._fontJson.glyphs
            : [];
        const glyphJson = glyphs.find((glyph) => glyph?.name === glyphName);
        const layerJson = Array.isArray(glyphJson?.layers)
            ? glyphJson.layers.find((layer) => layer?.id === layerId)
            : null;
        if (!layerJson || typeof layerJson !== 'object') {
            return null;
        }
        return layerJson;
    }
    _resettleDerivedLayersAfterOriginReplay(item) {
        const fontModel = this._getResettleFontModel();
        const originTargets = this._collectOriginLayerTargets(item, item.entries.filter((entry) => !isDerivedLayerChangePath(entry.path, item.originatingGlyphName)));
        if (!fontModel) {
            return originTargets;
        }
        if (originTargets.length) {
            this._syncPatchedLayerTargetsFromYDoc(originTargets);
        }
        const editKinds = deriveEditKindsFromChangeLogEntries(item.entries.filter((entry) => !isDerivedLayerChangePath(entry.path, item.originatingGlyphName)));
        if (editKinds.size === 0 || !originTargets.length) {
            return originTargets;
        }
        const sourceTarget = originTargets[0];
        const closure = computeLayerRecompositionClosure({
            sourceTargets: originTargets,
            editKinds,
            scope: 'all',
            fontModel,
            activeLayerId: sourceTarget?.layerId ?? item.originatingLayerId,
            sourceGlyphName: sourceTarget?.glyphName ?? item.originatingGlyphName,
            suppressor: this
        });
        const originatingGlyphName = item.originatingGlyphName ?? null;
        const derivedTargets = closure.recomposeTargets.filter((target) => {
            if (originatingGlyphName &&
                target.glyphName === originatingGlyphName) {
                return false;
            }
            return !!this._getFontJsonLayer(target.glyphName, target.layerId);
        });
        for (const target of derivedTargets) {
            this._applySettledLayerFromFontJson(target.glyphName, target.layerId);
        }
        return normalizeWorkerReplayTargets([
            ...originTargets,
            ...derivedTargets
        ]);
    }
    _applySettledLayerFromFontJson(glyphName, layerId) {
        const layerJson = this._getFontJsonLayer(glyphName, layerId);
        if (!layerJson) {
            return;
        }
        const glyphsMap = this.fontMap.get('glyphs');
        const glyphMap = glyphsMap instanceof Y.Map ? glyphsMap.get(glyphName) : null;
        const layersMap = glyphMap instanceof Y.Map ? glyphMap.get('layers') : null;
        const yLayerMap = layersMap instanceof Y.Map ? layersMap.get(layerId) : null;
        if (!(yLayerMap instanceof Y.Map)) {
            return;
        }
        const yLayerJson = fromYType(yLayerMap);
        const delta = { id: layerId };
        let hasChanges = false;
        for (const [key, value] of Object.entries(layerJson)) {
            if (JSON.stringify(value) === JSON.stringify(yLayerJson?.[key])) {
                continue;
            }
            delta[key] = cloneHistoryValue(value);
            hasChanges = true;
        }
        for (const key of Object.keys(yLayerJson || {})) {
            if (key === 'id' || key in layerJson) {
                continue;
            }
            delta[key] = null;
            hasChanges = true;
        }
        if (!hasChanges) {
            return;
        }
        this._applyLayerDelta(glyphName, layerId, delta);
    }
    _getHistoryReplayValue(entry, direction) {
        if (direction === 'undo') {
            return entry.replayOldValue ?? entry.oldValue;
        }
        return entry.replayNewValue ?? entry.newValue;
    }
    _cloneReplayValue(value) {
        return value === undefined ? undefined : cloneHistoryValue(value);
    }
    _prepareLayerSnapshotForHistory(layerId, layerJson, existingLayerJson) {
        return this._normalizeLayerSnapshot(layerId, this._prepareStorageValue(layerJson), existingLayerJson);
    }
    _prepareStorageValue(value) {
        const stripEditorIds = (candidate, isShapeEntry = false, isNodeEntry = false) => {
            if (Array.isArray(candidate)) {
                return candidate.map((item) => stripEditorIds(item, isShapeEntry, isNodeEntry));
            }
            if (!candidate || typeof candidate !== 'object') {
                return candidate;
            }
            const record = omitRestingLayerRuntimeKeys(candidate);
            const isPathOrComponent = isShapeEntry &&
                (Object.prototype.hasOwnProperty.call(record, 'nodes') ||
                    Object.prototype.hasOwnProperty.call(record, 'reference'));
            const storageRecord = isPathOrComponent || isNodeEntry
                ? (() => {
                    const { id: _id, ...withoutId } = record;
                    return withoutId;
                })()
                : { ...record };
            return Object.fromEntries(Object.entries(storageRecord).map(([key, item]) => [
                key,
                stripEditorIds(item, key === 'shapes', key === 'nodes')
            ]));
        };
        return stripEditorIds(cloneHistoryValue(value));
    }
    _cloneRuntimeValue(value) {
        return cloneHistoryValue(value);
    }
    _shouldReplayHistoryItemDirectly(item, direction) {
        if (!item) {
            return false;
        }
        const resettleDerivedLayers = shouldResettleDerivedLayersOnHistoryReplay(item, !!this._getResettleFontModel());
        return this._canReplayHistoryItemDirectly(item, direction, resettleDerivedLayers);
    }
    _canReplayHistoryItemDirectly(item, direction, originEntriesOnly = false) {
        const entries = originEntriesOnly
            ? item.entries.filter((entry) => !isDerivedLayerChangePath(entry.path, item.originatingGlyphName))
            : item.entries;
        if (!entries.length) {
            return false;
        }
        return entries.every((entry) => {
            // Direct replay supports 'set', 'add', and 'remove' ops.
            // 'add' on undo → deleteYPath (no replay value needed).
            // 'remove' on redo → deleteYPath (no replay value needed).
            // 'set' needs a replay value, except undo of a newly created
            // property (undefined old value) which deletes the path.
            const needsReplayValue = entry.op === 'set' ||
                (entry.op === 'remove' && direction === 'undo') ||
                (entry.op === 'add' && direction === 'redo');
            if (needsReplayValue) {
                const replayValue = direction === 'undo'
                    ? entry.replayOldValue
                    : entry.replayNewValue;
                if (replayValue === undefined) {
                    if (!(entry.op === 'set' && direction === 'undo')) {
                        return false;
                    }
                    // Newly created property: undo deletes it via history replay.
                }
                else {
                    const path = this._toYDocPath(this._parseEntryPath(entry.path));
                    if (this._isGlyphRootPath(path)) {
                        return !!replayValue && typeof replayValue === 'object';
                    }
                    if (path.length === 4 &&
                        path[0] === 'glyphs' &&
                        path[2] === 'layers' &&
                        typeof path[1] === 'string' &&
                        typeof path[3] === 'string') {
                        return !!replayValue && typeof replayValue === 'object';
                    }
                }
            }
            return true;
        });
    }
    _isGlyphRootPath(path) {
        return path.length === 2 && path[0] === 'glyphs' && !!path[1];
    }
    /** Glyph names in current font JSON array order (model / `_fontJson`). */
    _glyphNamesFromFontJson() {
        if (!this._fontJson) {
            return [];
        }
        const glyphs = this._fontJson.glyphs;
        if (!Array.isArray(glyphs)) {
            return [];
        }
        return glyphs
            .map((glyph) => glyph && typeof glyph === 'object'
            ? String(glyph.name || '').trim()
            : '')
            .filter((name) => name.length > 0);
    }
    _readYGlyphOrderNames() {
        const glyphOrder = this.fontMap.get('glyphOrder');
        if (glyphOrder instanceof Y.Array) {
            return glyphOrder.toArray().map(String);
        }
        return [];
    }
    /**
     * When the model array already reflects an add/remove, emit a `glyphOrder`
     * set so Yjs order matches insert position (not map-append order).
     */
    _createModelGlyphOrderSyncOperation(glyphName, mode) {
        const nextOrder = this._glyphNamesFromFontJson();
        const nameInModel = nextOrder.includes(glyphName);
        if (mode === 'add' && !nameInModel) {
            return null;
        }
        if (mode === 'remove' && nameInModel) {
            return null;
        }
        const oldOrder = this._readYGlyphOrderNames();
        if (oldOrder.length === nextOrder.length &&
            oldOrder.every((name, index) => name === nextOrder[index])) {
            return null;
        }
        return {
            op: 'set',
            path: ['glyphOrder'],
            oldValue: oldOrder,
            newValue: [...nextOrder]
        };
    }
    /**
     * Replace a Y.Array's contents without replacing the array object itself.
     * This keeps the shared container identity stable across windows.
     *
     * Uses LCS-based minimal diff (diffYArray) instead of full teardown
     * to produce smaller Yjs deltas for length-changing edits to
     * features.features, kern-group lists, and codepoints.
     */
    _replaceYArrayContents(targetArray, nextValues) {
        diffYArray(targetArray, nextValues);
    }
    _replaceYArrayEntry(targetArray, index, nextValue) {
        const currentValue = targetArray.get(index);
        const currentJsonValue = currentValue instanceof Y.Map || currentValue instanceof Y.Array
            ? fromYType(currentValue)
            : currentValue;
        if (this._isDeepEqual(currentJsonValue, nextValue)) {
            return;
        }
        if (Array.isArray(nextValue)) {
            if (currentValue instanceof Y.Array) {
                this._replaceYArrayContents(currentValue, nextValue);
            }
            else {
                targetArray.delete(index, 1);
                targetArray.insert(index, [toYType(nextValue)]);
            }
            return;
        }
        if (nextValue &&
            typeof nextValue === 'object' &&
            !Array.isArray(nextValue)) {
            const nextRecord = nextValue;
            if (currentValue instanceof Y.Map) {
                this._replaceYMapContents(currentValue, nextRecord);
            }
            else {
                targetArray.delete(index, 1);
                targetArray.insert(index, [toYType(nextRecord)]);
            }
            return;
        }
        targetArray.delete(index, 1);
        targetArray.insert(index, [nextValue]);
    }
    /**
     * Replace a Y.Map's entries in place, deleting keys absent from the
     * incoming snapshot and recursively reusing nested shared containers.
     */
    _replaceYMapContents(targetMap, nextRecord) {
        const nextKeys = new Set(Object.keys(nextRecord));
        targetMap.forEach((_value, key) => {
            if (!nextKeys.has(key)) {
                targetMap.delete(key);
            }
        });
        for (const [key, value] of Object.entries(nextRecord)) {
            this._replaceYMapEntry(targetMap, key, value);
        }
    }
    /**
     * Replace one Y.Map entry while preserving nested Y.Map/YArray objects
     * when the incoming value has the same container shape.
     */
    _replaceYMapEntry(targetMap, key, nextValue) {
        const currentValue = targetMap.get(key);
        const currentJsonValue = currentValue instanceof Y.Map || currentValue instanceof Y.Array
            ? fromYType(currentValue)
            : currentValue;
        if (this._isDeepEqual(currentJsonValue, nextValue)) {
            return;
        }
        if (Array.isArray(nextValue)) {
            if (currentValue instanceof Y.Array) {
                this._replaceYArrayContents(currentValue, nextValue);
            }
            else {
                targetMap.set(key, toYType(nextValue));
            }
            return;
        }
        if (nextValue &&
            typeof nextValue === 'object' &&
            !Array.isArray(nextValue)) {
            const nextRecord = nextValue;
            if (currentValue instanceof Y.Map) {
                this._replaceYMapContents(currentValue, nextRecord);
            }
            else {
                const nestedMap = new Y.Map();
                targetMap.set(key, nestedMap);
                this._replaceYMapContents(nestedMap, nextRecord);
            }
            return;
        }
        targetMap.set(key, nextValue);
    }
    _replaceLayerMapContents(layerMap, nextRecord) {
        const existingRecord = fromYType(layerMap);
        const sanitizedRecord = toRestingLayerJson(nextRecord, {
            existing: existingRecord,
            mode: 'replace',
            context: 'Y.Doc write'
        });
        const nextKeys = new Set(Object.keys(sanitizedRecord));
        const indexedStorageKeysToKeep = new Set();
        for (const key of nextKeys) {
            const mapping = INDEXED_MAP_KEYS[key];
            if (mapping) {
                indexedStorageKeysToKeep.add(mapping.byId);
                indexedStorageKeysToKeep.add(mapping.order);
            }
        }
        layerMap.forEach((_value, key) => {
            if (nextKeys.has(key) ||
                indexedStorageKeysToKeep.has(key) ||
                RESTING_LAYER_IDENTITY_KEYS.includes(key)) {
                return;
            }
            layerMap.delete(key);
        });
        for (const runtimeKey of RESTING_LAYER_RUNTIME_KEYS) {
            layerMap.delete(runtimeKey);
        }
        for (const [key, value] of Object.entries(sanitizedRecord)) {
            const mapping = INDEXED_MAP_KEYS[key];
            if (mapping) {
                layerMap.delete(key);
                if (Array.isArray(value)) {
                    applyIndexedMapArrayToYMap(layerMap, key, value);
                    continue;
                }
                layerMap.delete(mapping.byId);
                layerMap.delete(mapping.order);
            }
            this._replaceYMapEntry(layerMap, key, value);
        }
    }
    _applyGlyphSnapshot(glyphName, glyphSnapshot) {
        if (!glyphSnapshot || typeof glyphSnapshot !== 'object') {
            setYPath(this.fontMap, ['glyphs', glyphName], glyphSnapshot);
            return;
        }
        const glyphsMap = this.fontMap.get('glyphs');
        if (!(glyphsMap instanceof Y.Map)) {
            return;
        }
        let glyphMap = glyphsMap.get(glyphName);
        if (!(glyphMap instanceof Y.Map)) {
            glyphMap = new Y.Map();
            glyphsMap.set(glyphName, glyphMap);
        }
        const existingGlyphSnapshot = fromYType(glyphMap) ??
            this._fontJson?.glyphs?.find((glyph) => glyph?.name === glyphName);
        const glyphJson = this._normalizeGlyphSnapshot(glyphSnapshot, existingGlyphSnapshot);
        for (const [gk, gv] of Object.entries(glyphJson)) {
            if (gk === 'layers' && Array.isArray(gv)) {
                let layersMap = glyphMap.get('layers');
                if (!(layersMap instanceof Y.Map)) {
                    layersMap = new Y.Map();
                    glyphMap.set('layers', layersMap);
                }
                const nextLayerIds = new Set();
                for (const layerJson of gv) {
                    const layerId = layerJson.id ?? '';
                    if (!layerId) {
                        continue;
                    }
                    nextLayerIds.add(layerId);
                    // Patch the existing layerMap in place to preserve the
                    // Y.Map instance reference so layer UndoManagers (which
                    // register on the specific Y.Map object) remain valid
                    // after a glyph snapshot is applied.
                    let layerMap = layersMap.get(layerId);
                    if (!(layerMap instanceof Y.Map)) {
                        layerMap = new Y.Map();
                        layersMap.set(layerId, layerMap);
                    }
                    const normalizedLayerJson = this._normalizeLayerSnapshot(layerId, layerJson, fromYType(layerMap));
                    this._replaceLayerMapContents(layerMap, normalizedLayerJson);
                }
                // Remove layers that are no longer in the snapshot
                layersMap.forEach((_value, key) => {
                    if (!nextLayerIds.has(key)) {
                        layersMap?.delete(key);
                    }
                });
                setYPath(glyphMap, ['layerOrder'], Array.from(nextLayerIds));
                continue;
            }
            this._replaceYMapEntry(glyphMap, gk, gv);
        }
        // Remove glyph-level keys no longer in the snapshot
        const glyphKeys = new Set(Object.keys(glyphJson));
        if (Array.isArray(glyphJson.layers)) {
            glyphKeys.add('layerOrder');
        }
        glyphMap.forEach((_value, key) => {
            if (!glyphKeys.has(key)) {
                glyphMap?.delete(key);
            }
        });
    }
    _applyFontSnapshot(fontSnapshot) {
        if (!fontSnapshot ||
            typeof fontSnapshot !== 'object' ||
            Array.isArray(fontSnapshot)) {
            return;
        }
        const normalizedSnapshot = this._normalizeFontSnapshot(fontSnapshot, this._fontJson);
        const nextGlyphs = this._coerceFontGlyphSnapshots(normalizedSnapshot.glyphs);
        const nextGlyphNames = new Set();
        for (const glyph of nextGlyphs) {
            const glyphName = glyph && typeof glyph === 'object'
                ? String(glyph.name || '')
                : '';
            if (!glyphName) {
                continue;
            }
            nextGlyphNames.add(glyphName);
            this._applyGlyphSnapshot(glyphName, glyph);
        }
        const glyphsMap = this.fontMap.get('glyphs');
        if (glyphsMap instanceof Y.Map) {
            glyphsMap.forEach((_value, glyphName) => {
                if (!nextGlyphNames.has(glyphName)) {
                    glyphsMap.delete(glyphName);
                }
            });
        }
        setYPath(this.fontMap, ['glyphOrder'], [...nextGlyphNames]);
        const nextKeys = new Set(Object.keys(normalizedSnapshot));
        for (const [key, value] of Object.entries(normalizedSnapshot)) {
            if (key === 'glyphs' || key === 'glyphOrder') {
                continue;
            }
            this._replaceYMapEntry(this.fontMap, key, value);
        }
        this.fontMap.forEach((_value, key) => {
            if (key !== 'glyphs' &&
                key !== 'glyphOrder' &&
                !nextKeys.has(key)) {
                this.fontMap.delete(key);
            }
        });
    }
    _normalizeFontSnapshot(fontSnapshot, existingFontSnapshot) {
        if (!fontSnapshot ||
            typeof fontSnapshot !== 'object' ||
            Array.isArray(fontSnapshot)) {
            return fontSnapshot;
        }
        const existingFontRecord = existingFontSnapshot &&
            typeof existingFontSnapshot === 'object' &&
            !Array.isArray(existingFontSnapshot)
            ? cloneHistoryValue(existingFontSnapshot)
            : {};
        const normalizedFontRecord = cloneHistoryValue(fontSnapshot);
        if (Object.prototype.hasOwnProperty.call(normalizedFontRecord, 'glyphs')) {
            const incomingGlyphs = this._coerceFontGlyphSnapshots(normalizedFontRecord.glyphs);
            const existingGlyphs = this._coerceFontGlyphSnapshots(existingFontRecord.glyphs);
            const existingGlyphsByName = new Map(existingGlyphs
                .filter((glyph) => !!glyph && typeof glyph === 'object')
                .map((glyph) => [String(glyph.name || ''), glyph]));
            normalizedFontRecord.glyphs = incomingGlyphs
                .map((glyph) => {
                const glyphName = glyph && typeof glyph === 'object'
                    ? String(glyph.name || '')
                    : '';
                if (!glyphName) {
                    return null;
                }
                return this._normalizeGlyphSnapshot(glyph, existingGlyphsByName.get(glyphName));
            })
                .filter((glyph) => !!glyph);
        }
        return normalizedFontRecord;
    }
    _coerceFontGlyphSnapshots(glyphsSnapshot) {
        if (Array.isArray(glyphsSnapshot)) {
            return glyphsSnapshot.filter((glyph) => !!glyph &&
                typeof glyph === 'object' &&
                !Array.isArray(glyph));
        }
        if (glyphsSnapshot &&
            typeof glyphsSnapshot === 'object' &&
            !Array.isArray(glyphsSnapshot)) {
            return Object.entries(glyphsSnapshot)
                .filter(([, glyph]) => !!glyph && typeof glyph === 'object')
                .map(([glyphName, glyph]) => {
                const glyphRecord = cloneHistoryValue(glyph);
                if (typeof glyphRecord.name !== 'string' ||
                    !glyphRecord.name.length) {
                    glyphRecord.name = glyphName;
                }
                return glyphRecord;
            });
        }
        return [];
    }
    _normalizeGlyphSnapshot(glyphSnapshot, existingGlyphSnapshot) {
        if (!glyphSnapshot ||
            typeof glyphSnapshot !== 'object' ||
            Array.isArray(glyphSnapshot)) {
            return glyphSnapshot;
        }
        const existingGlyphRecord = existingGlyphSnapshot &&
            typeof existingGlyphSnapshot === 'object' &&
            !Array.isArray(existingGlyphSnapshot)
            ? cloneHistoryValue(existingGlyphSnapshot)
            : {};
        const incomingGlyphRecord = cloneHistoryValue(glyphSnapshot);
        const mergedGlyphRecord = {
            ...existingGlyphRecord,
            ...incomingGlyphRecord
        };
        if (Object.prototype.hasOwnProperty.call(incomingGlyphRecord, 'layers')) {
            const incomingLayers = this._coerceGlyphLayerSnapshots(incomingGlyphRecord.layers);
            const existingLayers = this._coerceGlyphLayerSnapshots(existingGlyphRecord.layers);
            const existingLayersById = new Map(existingLayers
                .filter((layer) => !!layer && typeof layer === 'object')
                .map((layer) => [String(layer.id || ''), layer]));
            mergedGlyphRecord.layers = incomingLayers
                .map((layer) => {
                const layerId = layer && typeof layer === 'object'
                    ? String(layer.id || '')
                    : '';
                if (!layerId) {
                    return null;
                }
                return this._normalizeLayerSnapshot(layerId, layer, existingLayersById.get(layerId));
            })
                .filter((layer) => !!layer);
        }
        return mergedGlyphRecord;
    }
    _coerceGlyphLayerSnapshots(layersSnapshot) {
        if (Array.isArray(layersSnapshot)) {
            return layersSnapshot.filter((layer) => !!layer &&
                typeof layer === 'object' &&
                !Array.isArray(layer));
        }
        if (layersSnapshot &&
            typeof layersSnapshot === 'object' &&
            !Array.isArray(layersSnapshot)) {
            return Object.entries(layersSnapshot)
                .filter(([, layer]) => !!layer && typeof layer === 'object')
                .map(([layerId, layer]) => {
                const layerRecord = cloneHistoryValue(layer);
                if (typeof layerRecord.id !== 'string' ||
                    !layerRecord.id.length) {
                    layerRecord.id = layerId;
                }
                return layerRecord;
            });
        }
        return [];
    }
    _canonicalizeFullStateRawFontJson() {
        const fontJson = this._fontJson;
        if (!fontJson ||
            typeof fontJson !== 'object' ||
            Array.isArray(fontJson)) {
            return;
        }
        const masterNameById = new Map();
        const masters = Array.isArray(fontJson.masters)
            ? fontJson.masters
            : [];
        for (const master of masters) {
            if (!master ||
                typeof master !== 'object' ||
                Array.isArray(master)) {
                continue;
            }
            const masterId = typeof master.id === 'string' && master.id.length
                ? master.id
                : '';
            const masterName = typeof master.name === 'string'
                ? master.name
                : master.name &&
                    typeof master.name === 'object' &&
                    typeof master.name.dflt ===
                        'string'
                    ? String(master.name.dflt || '')
                    : '';
            if (masterId && masterName) {
                masterNameById.set(masterId, masterName);
            }
        }
        const glyphs = Array.isArray(fontJson.glyphs)
            ? fontJson.glyphs
            : [];
        for (const glyph of glyphs) {
            if (!glyph || typeof glyph !== 'object' || Array.isArray(glyph)) {
                continue;
            }
            const layers = Array.isArray(glyph.layers)
                ? glyph.layers
                : [];
            for (const layer of layers) {
                if (!layer ||
                    typeof layer !== 'object' ||
                    Array.isArray(layer)) {
                    continue;
                }
                if (layer.height === undefined) {
                    delete layer.height;
                }
                if (layer.vertWidth === undefined) {
                    delete layer.vertWidth;
                }
                if (layer.isInterpolated === false) {
                    delete layer.isInterpolated;
                }
                if (layer.name === undefined) {
                    delete layer.name;
                }
                const master = layer.master;
                if (!master ||
                    typeof master !== 'object' ||
                    Array.isArray(master) ||
                    master.type !== 'DefaultForMaster' ||
                    typeof master.master !== 'string' ||
                    !master.master.length) {
                    continue;
                }
                const masterName = masterNameById.get(master.master);
                if (typeof layer.name === 'string' &&
                    (!layer.name.length ||
                        (masterName && layer.name === masterName))) {
                    delete layer.name;
                }
            }
        }
    }
    _getKnownMasterIds() {
        const masterIds = new Set();
        const masters = this._fontJson?.masters;
        if (!Array.isArray(masters)) {
            return masterIds;
        }
        for (const master of masters) {
            const masterId = master && typeof master === 'object'
                ? String(master.id || '')
                : '';
            if (masterId) {
                masterIds.add(masterId);
            }
        }
        return masterIds;
    }
    _normalizeLayerMasterSnapshot(layerId, layerRecord, existingLayerRecord) {
        const normalizeMasterValue = (masterValue) => {
            if (!masterValue || typeof masterValue !== 'object') {
                if (typeof masterValue === 'string' && masterValue.length) {
                    return {
                        type: 'DefaultForMaster',
                        master: masterValue
                    };
                }
                return null;
            }
            if (Array.isArray(masterValue)) {
                return null;
            }
            const masterRecord = cloneHistoryValue(masterValue);
            if (masterRecord.type === 'FreeFloating') {
                return { type: 'FreeFloating' };
            }
            if ((masterRecord.type === 'DefaultForMaster' ||
                masterRecord.type === 'AssociatedWithMaster') &&
                typeof masterRecord.master === 'string' &&
                masterRecord.master.length) {
                return {
                    type: masterRecord.type,
                    master: masterRecord.master
                };
            }
            if (typeof masterRecord.master === 'string' &&
                masterRecord.master.length) {
                return {
                    type: 'DefaultForMaster',
                    master: masterRecord.master
                };
            }
            if (typeof masterRecord.DefaultForMaster === 'string' &&
                masterRecord.DefaultForMaster.length) {
                return {
                    type: 'DefaultForMaster',
                    master: masterRecord.DefaultForMaster
                };
            }
            if (typeof masterRecord.default_for_master === 'string' &&
                masterRecord.default_for_master.length) {
                return {
                    type: 'DefaultForMaster',
                    master: masterRecord.default_for_master
                };
            }
            if (typeof masterRecord.AssociatedWithMaster === 'string' &&
                masterRecord.AssociatedWithMaster.length) {
                return {
                    type: 'AssociatedWithMaster',
                    master: masterRecord.AssociatedWithMaster
                };
            }
            if (typeof masterRecord.associated_with_master === 'string' &&
                masterRecord.associated_with_master.length) {
                return {
                    type: 'AssociatedWithMaster',
                    master: masterRecord.associated_with_master
                };
            }
            if ('FreeFloating' in masterRecord) {
                return { type: 'FreeFloating' };
            }
            return null;
        };
        const normalizedMaster = normalizeMasterValue(layerRecord.master) ??
            normalizeMasterValue(existingLayerRecord.master);
        if (normalizedMaster) {
            layerRecord.master = normalizedMaster;
            return;
        }
        if (layerRecord.is_background !== true &&
            this._getKnownMasterIds().has(layerId)) {
            layerRecord.master = {
                type: 'DefaultForMaster',
                master: layerId
            };
        }
    }
    _normalizeLayerSnapshot(layerId, layerSnapshot, existingLayerSnapshot, preserveMissingKeys = true, isExistingFresh) {
        if (!layerSnapshot ||
            typeof layerSnapshot !== 'object' ||
            Array.isArray(layerSnapshot)) {
            return layerSnapshot;
        }
        const existingLayerRecord = existingLayerSnapshot &&
            typeof existingLayerSnapshot === 'object' &&
            !Array.isArray(existingLayerSnapshot)
            ? isExistingFresh
                ? existingLayerSnapshot
                : cloneHistoryValue(existingLayerSnapshot)
            : {};
        const incomingLayerRecord = cloneHistoryValue(layerSnapshot);
        const mergedLayerRecord = preserveMissingKeys
            ? {
                ...existingLayerRecord,
                ...incomingLayerRecord
            }
            : { ...incomingLayerRecord };
        if (typeof mergedLayerRecord.id !== 'string' ||
            !mergedLayerRecord.id.length) {
            mergedLayerRecord.id = layerId;
        }
        this._normalizeLayerMasterSnapshot(layerId, mergedLayerRecord, existingLayerRecord);
        const layerMaster = mergedLayerRecord.master;
        if (layerMaster &&
            typeof layerMaster === 'object' &&
            !Array.isArray(layerMaster) &&
            layerMaster.type === 'DefaultForMaster' &&
            typeof layerMaster.master === 'string' &&
            typeof mergedLayerRecord.name === 'string') {
            const masterName = Array.isArray(this._fontJson?.masters)
                ? this._fontJson.masters.find((master) => master?.id === layerMaster.master)?.name
                : undefined;
            const normalizedMasterName = typeof masterName === 'string'
                ? masterName
                : masterName && typeof masterName === 'object'
                    ? String(masterName.dflt || '')
                    : '';
            if (!mergedLayerRecord.name.length ||
                (normalizedMasterName &&
                    mergedLayerRecord.name === normalizedMasterName)) {
                delete mergedLayerRecord.name;
            }
        }
        if (typeof mergedLayerRecord.width !== 'number' ||
            !Number.isFinite(mergedLayerRecord.width)) {
            const incomingHasWidth = Object.prototype.hasOwnProperty.call(incomingLayerRecord, 'width');
            const existingWidth = existingLayerRecord.width;
            if (!incomingHasWidth &&
                typeof existingWidth === 'number' &&
                Number.isFinite(existingWidth)) {
                mergedLayerRecord.width = existingWidth;
            }
            else {
                throw new Error(`[PatchSyncEngine] Layer ${layerId} has invalid width; refusing to normalize malformed layer snapshot.`);
            }
        }
        for (const [key, value] of Object.entries(mergedLayerRecord)) {
            if (value === undefined) {
                delete mergedLayerRecord[key];
            }
        }
        if (mergedLayerRecord.isInterpolated === false) {
            delete mergedLayerRecord.isInterpolated;
        }
        return toRestingLayerJson(mergedLayerRecord, {
            existing: existingLayerRecord,
            mode: 'replace',
            context: 'history snapshot'
        });
    }
    /**
     * Apply a sparse layer delta to the Y.Doc.
     *
     * The delta contains ONLY the fields that changed.  Fields absent
     * from the delta are left untouched in the Y.Doc — this is the
     * fundamental contract that prevents key loss during cross-window
     * and cloud sync.
     *
     * Scalar/object fields in the delta replace the existing value.
     * Array fields (shapes, anchors, guides) in the delta replace the
     * entire array.  Fields with value `null` are deleted from the
     * Y.Map.
     */
    _applyLayerDelta(glyphName, layerId, delta) {
        const glyphsMap = this.fontMap.get('glyphs');
        if (!(glyphsMap instanceof Y.Map)) {
            return;
        }
        const glyphMap = glyphsMap.get(glyphName);
        if (!(glyphMap instanceof Y.Map)) {
            return;
        }
        const layersMap = ensureGlyphLayersMap(glyphMap);
        if (!delta || typeof delta !== 'object' || Array.isArray(delta)) {
            console.warn(`[PatchSyncEngine] Ignoring malformed layer delta for ${glyphName}/${layerId}; expected object payload.`);
            return;
        }
        const deltaRecord = delta;
        // Empty delta → nothing to apply.
        const deltaKeys = Object.keys(deltaRecord);
        if (deltaKeys.length === 0) {
            return;
        }
        const existingLayerValue = layersMap.get(layerId);
        let layerMap = existingLayerValue instanceof Y.Map
            ? existingLayerValue
            : undefined;
        // Creating a new layer from a sparse delta requires width and master.
        if (!(layerMap instanceof Y.Map)) {
            const hasWidth = typeof deltaRecord.width === 'number' &&
                Number.isFinite(deltaRecord.width);
            const hasMaster = !!deltaRecord.master &&
                typeof deltaRecord.master === 'object' &&
                !Array.isArray(deltaRecord.master);
            if (!hasWidth || !hasMaster) {
                console.warn(`[PatchSyncEngine] Ignoring sparse layer delta for missing ${glyphName}/${layerId}; cannot create layer root from sparse payload.`);
                return;
            }
            layerMap = new Y.Map();
            layersMap.set(layerId, layerMap);
        }
        // Read existing Y.Doc state for master normalization context.
        const existingYDocRecord = fromYType(layerMap);
        // Clone the delta — _normalizeLayerMasterSnapshot mutates it.
        const workingRecord = toRestingLayerJson(cloneHistoryValue(deltaRecord), {
            existing: existingYDocRecord,
            mode: 'delta',
            context: 'Y.Doc write'
        });
        // Normalize master if it changed.
        if ('master' in workingRecord) {
            this._normalizeLayerMasterSnapshot(layerId, workingRecord, existingYDocRecord ?? {});
        }
        // Validate width if it's in the delta.
        if ('width' in workingRecord) {
            if (typeof workingRecord.width !== 'number' ||
                !Number.isFinite(workingRecord.width)) {
                console.warn(`[PatchSyncEngine] Layer delta for ${glyphName}/${layerId} has invalid width; refusing to apply width.`);
                delete workingRecord.width;
            }
        }
        // Validate master if it's in the delta.
        if ('master' in workingRecord && !workingRecord.master) {
            console.warn(`[PatchSyncEngine] Layer delta for ${glyphName}/${layerId} has invalid master; refusing to apply master.`);
            delete workingRecord.master;
        }
        // Strip isInterpolated if set to false in the delta.
        if (workingRecord.isInterpolated === false) {
            workingRecord.isInterpolated = null;
        }
        for (const runtimeKey of RESTING_LAYER_RUNTIME_KEYS) {
            layerMap.delete(runtimeKey);
        }
        // Apply each delta key to the Y.Map.
        for (const [key, value] of Object.entries(workingRecord)) {
            if (value === null || value === undefined) {
                if (RESTING_LAYER_IDENTITY_KEYS.includes(key)) {
                    continue;
                }
                // For indexed-map keys, delete the *ById+*Order keys
                // so downstream readers see the data as absent (not
                // empty), preserving merge semantics.
                if (INDEXED_MAP_KEYS[key]) {
                    const mapping = INDEXED_MAP_KEYS[key];
                    layerMap.delete(mapping.byId);
                    layerMap.delete(mapping.order);
                }
                else {
                    layerMap.delete(key);
                }
            }
            else {
                // Anchors/guides are stored as indexed maps. Shapes are stored
                // as upstream-truthful plain arrays with string-node paths.
                if ((key === 'anchors' || key === 'guides') &&
                    Array.isArray(value)) {
                    applyIndexedMapArrayToYMap(layerMap, key, value);
                    continue;
                }
                this._replaceYMapEntry(layerMap, key, value);
            }
        }
    }
    _targetFromHistoryItem(item, fallbackGlyphName, fallbackLayerId) {
        const glyphNames = deriveGlyphNamesFromPaths(item.touchedPaths);
        const layerIds = deriveLayerIdsFromPaths(item.touchedPaths);
        if (item.undoScope === 'layer') {
            return {
                glyphName: glyphNames[0] ?? fallbackGlyphName,
                layerId: layerIds[0] ?? fallbackLayerId
            };
        }
        if (item.undoScope === 'glyph') {
            return {
                glyphName: glyphNames[0] ?? fallbackGlyphName,
                layerId: null
            };
        }
        return {
            glyphName: null,
            layerId: null
        };
    }
    _getUndoManagerForTarget(target) {
        if (target.glyphName && target.layerId) {
            return {
                manager: this.getLayerUndoManager(target.glyphName, target.layerId),
                scope: 'layer'
            };
        }
        if (target.glyphName) {
            return {
                manager: this.getGlyphUndoManager(target.glyphName),
                scope: 'glyph'
            };
        }
        return {
            manager: this._fontUndoManager,
            scope: 'font'
        };
    }
    _appendChangeLogEntry(entry) {
        this._changeLog.push(normalizeChangeLogEntry(entry));
        this._notifyChangeLogListeners();
    }
    _appendChangeLogEntries(entries) {
        if (!entries.length) {
            return;
        }
        this._changeLog.push(...entries.map((entry) => normalizeChangeLogEntry(entry)));
        this._notifyChangeLogListeners();
    }
    _deriveForwardChangesFromChangeLogEntries(entries) {
        return entries.map((entry) => ({
            path: entry.path,
            op: entry.op,
            oldValue: cloneHistoryValue(entry.replayOldValue === undefined
                ? entry.oldValue
                : entry.replayOldValue),
            newValue: cloneHistoryValue(entry.replayNewValue === undefined
                ? entry.newValue
                : entry.replayNewValue),
            objectType: deriveObjectInfoFromPath(entry.path).objectType
        }));
    }
    _deriveForwardChangesFromCollaborationMessage(message) {
        return message.changes.map((change) => ({
            path: change.path,
            op: change.op,
            oldValue: cloneHistoryValue(change.replayOldValue),
            newValue: cloneHistoryValue(change.replayNewValue),
            objectType: deriveObjectInfoFromPath(change.path).objectType
        }));
    }
    _createCollaborationLogItem(message, update, direction, derivedForwardChanges) {
        const originating = resolveCollaborationOriginatingLayer(message.metadata.undoScope, message.changes.length
            ? message.changes.map((change, index) => ({
                path: change.path,
                originatingGlyphName: index === 0
                    ? (message.metadata.originatingGlyphName ?? null)
                    : null,
                originatingLayerId: index === 0
                    ? (message.metadata.originatingLayerId ?? null)
                    : null
            }))
            : [
                {
                    path: '',
                    originatingGlyphName: message.metadata.originatingGlyphName ?? null,
                    originatingLayerId: message.metadata.originatingLayerId ?? null
                }
            ]);
        return {
            id: collaborationMessageKey(message),
            direction,
            timestamp: message.timestamp,
            transactionDurationMs: message.metadata.transactionDurationMs ?? null,
            summary: message.summary,
            label: message.label,
            source: message.source,
            editSource: message.metadata.editSource ?? null,
            windowId: message.windowId,
            windowRoleLabel: message.metadata.sourceWindowRoleLabel ??
                this._getWindowRoleLabel(),
            historyItemId: message.metadata.historyItemId,
            promptGroupId: message.metadata.promptGroupId ?? null,
            historyAction: message.metadata.historyAction,
            targetHistoryItemId: message.metadata.targetHistoryItemId ?? null,
            undoScope: message.metadata.undoScope,
            undoSurfaceAffinity: message.metadata.undoSurfaceAffinity ?? null,
            historyTargetKey: message.metadata.historyTargetKey ?? null,
            historyTargetLabel: message.metadata.historyTargetLabel ?? null,
            originatingGlyphName: originating.glyphName,
            originatingLayerId: originating.layerId,
            updateByteLength: update.byteLength,
            updateBase64Preview: this._toUpdateBase64Preview(update),
            changedGlyphNames: [...message.metadata.changedGlyphNames],
            changedLayerIds: [...message.metadata.changedLayerIds],
            workerReplayTargets: normalizeWorkerReplayTargets(message.metadata.workerReplayTargets),
            changes: message.changes.map((change) => ({
                ...change,
                workerReplayTargets: change.workerReplayTargets
                    ? [...change.workerReplayTargets]
                    : undefined
            })),
            derivedForwardChanges
        };
    }
    _appendCollaborationLogItems(items) {
        if (!items.length) {
            return;
        }
        const existingIds = new Set(this._collaborationLog.map((item) => item.id));
        let didAddItem = false;
        let lastTimestamp = this._collaborationLog.length > 0
            ? this._collaborationLog[this._collaborationLog.length - 1]
                .timestamp
            : Number.NEGATIVE_INFINITY;
        let requiresSort = false;
        for (const item of items) {
            if (existingIds.has(item.id)) {
                continue;
            }
            if (item.timestamp < lastTimestamp) {
                requiresSort = true;
            }
            this._collaborationLog.push(item);
            existingIds.add(item.id);
            lastTimestamp = item.timestamp;
            didAddItem = true;
        }
        if (!didAddItem) {
            return;
        }
        if (requiresSort) {
            this._collaborationLog.sort((left, right) => left.timestamp - right.timestamp);
        }
        this._notifyCollaborationLogListeners();
    }
    _notifyCollaborationLogListeners() {
        const items = this.getCollaborationLog();
        for (const listener of this._collaborationLogListeners) {
            listener(items);
        }
    }
    _toUpdateBase64Preview(update) {
        let binary = '';
        const previewLength = Math.min(update.length, 96);
        for (let index = 0; index < previewLength; index++) {
            binary += String.fromCharCode(update[index]);
        }
        const base64 = btoa(binary);
        return update.length > previewLength ? `${base64}...` : base64;
    }
    _notifyChangeLogListeners() {
        const entries = this.getChangeLog();
        for (const listener of this._changeLogListeners) {
            listener(entries);
        }
    }
}
function cloneHistoryValue(value) {
    if (value === undefined) {
        return value;
    }
    return JSON.parse(JSON.stringify(value));
}
